// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { useState, useCallback, useMemo, useEffect, useRef, type ReactNode } from 'react';
import { apiClient } from '../services/api-client.js';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useLocale } from '../hooks/useLocale.js';
import { useInstalledLanguages } from '../hooks/useInstalledLanguages.js';
import { useAuth } from '../contexts/AuthContext.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import { useModuleConfig } from '../contexts/ModuleConfigContext.js';
import {
  SIDEBAR_PERMISSION_MAP,
  SIDEBAR_MODULE_MAP,
  isLinkVisible,
} from '../config/route-permissions.js';
import {
  SIDEBAR_CATEGORIES,
  categoryFlattensSections,
  type SidebarCategoryKey,
} from '../config/sidebar-categories.js';
import { OVERLAY_REGISTER_VIEWS } from '../config/overlay-register-surfaces.js';
import {
  operationalOverlayViewIds,
  useSectorOverlayNavigation,
} from '../hooks/useSectorOverlayNavigation.js';
import { Permission, extractLocale } from '@vn-grc/shared';
import { SkipToContent } from '../components/SkipToContent.js';
import { NotificationBell } from '../components/NotificationBell.js';
import { UIAssistProvider } from '../modules/ui-assist/components/UIAssistProvider.js';
import { TourProvider, useTourContext } from '../contexts/TourContext.js';
import { BottomRightDock, BottomRightDockProvider } from '../components/layout/BottomRightDock.js';
import { ForsetiPageProvider } from '../contexts/ForsetiPageContext.js';
import { ForsetiLauncher } from '../components/forseti/ForsetiLauncher.js';
import { TourRunner } from '../components/TourRunner.js';
import { useToast } from '../components/Toast.js';
import { extractErrorMessage } from '../utils/api-helpers.js';
import { ForsetiIcon } from '../components/icons/ActionIcons.js';

interface MenuLeaf {
  label: string;
  to: string;
  requiredPermission?: string;
  testId?: string;
  /**
   * Sprint 63.22.0 gate E12.5 pass 2 — a page about the viewer's OWN work
   * (their review queue, their notification preferences). It is shown without
   * its section's gate: the leaf's own gate and its route's gate still apply,
   * so it is shown exactly when the page it opens would render.
   */
  selfService?: true;
  /**
   * The `MenuIcon` drawn before the label when the link sits directly under a
   * category-style header (a flattened category, or Forseti / Onboarding /
   * Platform). Deeper links are drawn without one, as in every category.
   */
  icon?: string;
}

interface MenuSubGroup {
  label: string;
  children: MenuLeaf[];
  /** As `MenuLeaf.icon`: drawn when the group sits directly under a category-style header. */
  icon?: string;
}

type MenuChild = MenuLeaf | MenuSubGroup;

function isSubGroup(child: MenuChild): child is MenuSubGroup {
  return (child as MenuSubGroup).children !== undefined;
}

interface MenuItem {
  label: string;
  to?: string;
  icon?: string;
  requiredPermission?: string;
  moduleId?: string;
  children?: MenuChild[];
  /**
   * Sprint 63.42.2 (AC3) — the section's sidebar category and help slug,
   * declared when its first leaf's path would file it wrongly: the Regulatory
   * section's first leaf is `/admin/localization/frameworks`, a Localization
   * path (the derived-path trap of the Module 30 plan §3). `getCategoryForItem`
   * reads `category`; `export-sidebar-menu-tree.ts` reads both, as literals.
   */
  category?: SidebarCategoryKey;
  helpSlug?: string;
}

/**
 * Flatten a module's children into all leaf entries, whether nested under a
 * sub-group or at the top level. Used for active-route detection, permission
 * filtering, and `needsEnd` resolution for NavLinks.
 */
function flattenLeaves(children?: MenuChild[]): MenuLeaf[] {
  if (!children) return [];
  const leaves: MenuLeaf[] = [];
  for (const c of children) {
    if (isSubGroup(c)) leaves.push(...c.children);
    else leaves.push(c);
  }
  return leaves;
}

/**
 * Sprint 63.43.0 gate E12.5 pass 1 (P3-1) — a section's open state is keyed by
 * what the section IS, not by its index among the visible sections: the Sector
 * Overlays section can appear after the navigation projection loads, and an
 * index key then moved every later section's open state to its neighbour. The
 * key is the section's first leaf route in the declared (unfiltered) menu,
 * which no locale or visibility change moves; a link section uses its own route.
 */
function sectionKey(item: MenuItem): string {
  return flattenLeaves(item.children)[0]?.to ?? item.to ?? item.label;
}

/** A sub-group's key: its section's key and the sub-group's first leaf route. */
function subGroupKey(item: MenuItem, group: MenuSubGroup): string {
  return `${sectionKey(item)}::${group.children[0]?.to ?? group.label}`;
}

const CATEGORY_STORAGE_KEY = 'grc_sidebar_categories';

/**
 * The fold-state key of a standalone section drawn like a category (Forseti,
 * Onboarding, Platform): kept with the categories' state, so it is remembered
 * between visits the same way. Prefixed so it can never equal a category key.
 */
function standaloneFoldKey(item: MenuItem): string {
  return `section:${sectionKey(item)}`;
}

/**
 * Sprint 63.43.0 (owner ruling OR-26, AC2) — the overlay-exclusive (Class A)
 * registry views, each a leaf under a Sector Overlays sector group. Such a leaf
 * is drawn only while an operational overlay declares into its view.
 */
const SECTOR_OVERLAY_LEAF_VIEWS = OVERLAY_REGISTER_VIEWS.filter(
  (view) => view.sectorGroup !== undefined,
);

function getItemPath(item: MenuItem): string {
  if (item.to) return item.to;
  const firstLeaf = flattenLeaves(item.children)[0];
  if (firstLeaf?.to) {
    const parts = firstLeaf.to.split('/');
    return '/' + parts.slice(1, 3).join('/');
  }
  return '';
}

function getCategoryForItem(item: MenuItem): string | null {
  if (item.category) return item.category;
  const path = getItemPath(item);
  for (const cat of SIDEBAR_CATEGORIES) {
    if (cat.modules.some((m) => path.startsWith(m))) return cat.key;
  }
  return null;
}

/** Whether `pathname` is the leaf's route or a page under it (a path-segment boundary). */
function leafMatches(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(to.endsWith('/') ? to : to + '/');
}

interface ActiveTrail {
  item: MenuItem;
  group: MenuSubGroup | null;
  /** The leaf the current URL opens: the one sidebar link that is current. */
  leaf: MenuLeaf;
  category: string | null;
}

/**
 * Sprint 63.42.2 (AC2) — the ancestors of the leaf the current URL opens,
 * read from the menu tree: the leaf whose route is the longest match wins,
 * and its section, group and category are what expand. Category path
 * prefixes cannot decide this: the Overlay Catalog's URL is under
 * `/admin/localization` while its leaf is in Sector Overlays, and most sector
 * register URLs match no category prefix at all.
 *
 * Only a leaf the sidebar draws can win (`isLeafVisible`, the renderer's own
 * rule): a sector leaf hidden because no operational overlay declares into it,
 * or a leaf whose own or section gate is not held, must not pull its hidden
 * category open over the drawn link the URL also matches (gate E12.5 pass 1,
 * F1 — `/admin/sector-licences/applications` with no Digital Technology
 * overlay is Regulatory › Regulatory Registers › Sector Licences).
 */
function findActiveTrail(
  items: MenuItem[],
  pathname: string,
  isLeafVisible: (item: MenuItem, leaf: MenuLeaf) => boolean,
): ActiveTrail | null {
  let best: { trail: ActiveTrail; length: number } | null = null;
  for (const item of items) {
    for (const child of item.children ?? []) {
      const entries: Array<[MenuLeaf, MenuSubGroup | null]> = isSubGroup(child)
        ? child.children.map((leaf) => [leaf, child])
        : [[child, null]];
      for (const [leaf, group] of entries) {
        if (!leafMatches(pathname, leaf.to)) continue;
        if (best && best.length >= leaf.to.length) continue;
        if (!isLeafVisible(item, leaf)) continue;
        best = {
          trail: { item, group, leaf, category: getCategoryForItem(item) },
          length: leaf.to.length,
        };
      }
    }
  }
  return best?.trail ?? null;
}

/**
 * The categories' fold state: every category folded, with the person's saved
 * choices laid over it. A saved state written before a category existed has no
 * key for it, and that category is folded like it is for a new person (gate
 * E12.5 pass 1, F6 — Sector Overlays opened by default for every returning
 * user). The active category still opens on navigation.
 */
function allFolded(): Record<string, boolean> {
  const state: Record<string, boolean> = {};
  for (const cat of SIDEBAR_CATEGORIES) state[cat.key] = false;
  return state;
}

function loadCategoryState(): Record<string, boolean> {
  const state = allFolded();
  try {
    const raw = localStorage.getItem(CATEGORY_STORAGE_KEY);
    const saved: unknown = raw ? JSON.parse(raw) : null;
    if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
      for (const [key, open] of Object.entries(saved as Record<string, unknown>)) {
        if (typeof open === 'boolean') state[key] = open;
      }
    }
  } catch {
    // Storage absent, blocked or malformed: a presentation cache with no
    // authority effect, so every category falls back to folded.
    return allFolded();
  }
  return state;
}

function saveCategoryState(state: Record<string, boolean>): void {
  try {
    localStorage.setItem(CATEGORY_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

/** One 18×18 sidebar glyph: the stroke style every `MenuIcon` uses. */
function NavGlyph({ children }: { children: ReactNode }) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        {children}
      </g>
    </svg>
  );
}

/** Simple inline SVG icons for sidebar menu items (AesirX style). */
const MenuIcon = ({ name }: { name: string }) => {
  const icons: Record<string, JSX.Element> = {
    dashboard: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="2" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <rect x="10" y="2" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <rect x="2" y="10" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <rect x="10" y="8" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
    rights: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M9 2v14M5 6l4-4 4 4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    data: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <ellipse cx="9" cy="5" rx="6" ry="2.5" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M3 5v8c0 1.38 2.69 2.5 6 2.5s6-1.12 6-2.5V5"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path d="M3 9c0 1.38 2.69 2.5 6 2.5s6-1.12 6-2.5" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
    governance: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M9 2L3 5v4c0 4 3 6.5 6 7.5 3-1 6-3.5 6-7.5V5L9 2z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M7 9l2 2 3-4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    consent: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="3" y="3" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M6 9l2 2 4-4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    assessments: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M4 14V8M9 14V4M14 14V10"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
    classification: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M3 4h12M3 9h8M3 14h5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="14" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
    monitoring: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M9 6v3l2 2"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    incidents: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M9 3l7 12H2L9 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M9 8v3M9 13v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    connectors: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M4 7h3v4H4zM11 7h3v4h-3z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path d="M7 9h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M2 9H4M14 9h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    onboarding: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.5" />
        <path d="M9 6v4M9 12h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    audit: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M5 3h8a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M6 7h6M6 10h6M6 13h3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    people: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M1 15c0-2.8 2.2-5 5-5s5 2.2 5 5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="13" cy="6" r="2" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M17 15c0-2.2-1.8-4-4-4-1 0-1.9.4-2.6 1"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    platform: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="6" r="3" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M3 16c0-3.3 2.7-6 6-6s6 2.7 6 6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    settings: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M9 2v2M9 14v2M2 9h2M14 9h2M4.2 4.2l1.4 1.4M12.4 12.4l1.4 1.4M4.2 13.8l1.4-1.4M12.4 5.6l1.4-1.4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    deletion: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M5 4V3a1 1 0 011-1h6a1 1 0 011 1v1M3 4h12M5 4v10a2 2 0 002 2h4a2 2 0 002-2V4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M8 7v5M10 7v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    discovery: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 12l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M8 5.5v5M5.5 8h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    help: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M7 7a2 2 0 013.5 1.5c0 1-1.5 1.5-1.5 2.5M9 13.5h.01"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    globe: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M2 9h14M9 2c2.5 2.5 3 5 3 7s-.5 4.5-3 7M9 2c-2.5 2.5-3 5-3 7s.5 4.5 3 7"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    ),
    // Sprint 61.0.10 — sidebar category headers. Distinct per category, same
    // 18×18 / 1.5 stroke grammar as the module icons above.
    category_privacy: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M5 2h6l3 3v11a1 1 0 01-1 1H5a1 1 0 01-1-1V3a1 1 0 011-1z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path d="M11 2v3h3" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path
          d="M9 7.5l2.5 1v2c0 1.5-1.1 2.6-2.5 3-1.4-.4-2.5-1.5-2.5-3v-2l2.5-1z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    ),
    category_vendor: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="6" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="12.5" cy="6.5" r="2" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M2 15v-1a4 4 0 014-4h0a4 4 0 014 4v1"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M11 10.5h1.5a3.5 3.5 0 013.5 3.5v1"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    category_risk: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M9 2.5l7 12.5H2L9 2.5z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path d="M9 7v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="9" cy="12.75" r="0.75" fill="currentColor" />
      </svg>
    ),
    category_operations: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="2.25" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M9 2.5v1.8M9 13.7v1.8M2.5 9h1.8M13.7 9h1.8M4.4 4.4l1.3 1.3M12.3 12.3l1.3 1.3M4.4 13.6l1.3-1.3M12.3 5.7l1.3-1.3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle
          cx="9"
          cy="9"
          r="5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeDasharray="2.6 1.4"
        />
      </svg>
    ),
    category_workflow: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M7 4.5h8M7 9h8M7 13.5h8"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M2.5 4.5l1 1 1.5-2"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M2.5 9l1 1 1.5-2"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect
          x="2.5"
          y="12.25"
          width="2.5"
          height="2.5"
          rx="0.5"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
    ),
    // Sprint 63.42.2 — the Sector Overlays category: stacked layers, an
    // overlay laid over the base, distinct from the other category icons.
    category_sector_overlays: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M9 2.5l6.5 3.25L9 9 2.5 5.75 9 2.5z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="M2.5 9L9 12.25 15.5 9"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M2.5 12.25L9 15.5l6.5-3.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    // Sprint 63.42.2 — the Regulatory section: a balance (the law's scales).
    regulatory: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M9 2.5v12.5M6 15h6M3.5 5h11"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M3.5 5L1.75 9.5h3.5L3.5 5zM14.5 5l-1.75 4.5h3.5L14.5 5z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    ),
    // Owner request 2026-11-03 — the entries under Forseti, Onboarding and
    // Platform, and the Forseti star (the launcher's glyph, reused).
    forseti: <ForsetiIcon size={18} />,
    chat: (
      <NavGlyph>
        <path d="M3 4.5A1.5 1.5 0 014.5 3h9A1.5 1.5 0 0115 4.5v6a1.5 1.5 0 01-1.5 1.5H8l-3.5 3v-3A1.5 1.5 0 013 10.5z" />
      </NavGlyph>
    ),
    folder: (
      <NavGlyph>
        <path d="M2.5 5A1.5 1.5 0 014 3.5h3l1.5 2h5.5A1.5 1.5 0 0115.5 7v6a1.5 1.5 0 01-1.5 1.5H4A1.5 1.5 0 012.5 13z" />
      </NavGlyph>
    ),
    workflow: (
      <NavGlyph>
        <rect x="2.5" y="2.5" width="5" height="4" rx="1" />
        <rect x="10.5" y="11.5" width="5" height="4" rx="1" />
        <path d="M5 6.5v3a2 2 0 002 2h3.5" />
      </NavGlyph>
    ),
    table: (
      <NavGlyph>
        <rect x="2.5" y="3" width="13" height="12" rx="1.5" />
        <path d="M2.5 7h13M2.5 11h13M7 7v8" />
      </NavGlyph>
    ),
    document_check: (
      <NavGlyph>
        <path d="M5 2h5.5L14 5.5V15a1 1 0 01-1 1H5a1 1 0 01-1-1V3a1 1 0 011-1z" />
        <path d="M6.5 10.5l1.75 1.75L11.5 9" />
      </NavGlyph>
    ),
    lineage: (
      <NavGlyph>
        <circle cx="4.5" cy="4" r="1.75" />
        <circle cx="4.5" cy="14" r="1.75" />
        <circle cx="13.5" cy="9" r="1.75" />
        <path d="M4.5 5.75v6.5M4.5 9h7.25" />
      </NavGlyph>
    ),
    overlap: (
      <NavGlyph>
        <circle cx="6.75" cy="9" r="4.25" />
        <circle cx="11.25" cy="9" r="4.25" />
      </NavGlyph>
    ),
    draft: (
      <NavGlyph>
        <path d="M11.5 3.5l3 3L7 14H4v-3z" />
        <path d="M10 5l3 3" />
      </NavGlyph>
    ),
    chart: (
      <NavGlyph>
        <path d="M3 15h12" />
        <path d="M5.5 12V8M9 12V4.5M12.5 12V9.5" />
      </NavGlyph>
    ),
    chip: (
      <NavGlyph>
        <rect x="5" y="5" width="8" height="8" rx="1" />
        <path d="M7.5 2.5V5M10.5 2.5V5M7.5 13v2.5M10.5 13v2.5M2.5 7.5H5M2.5 10.5H5M13 7.5h2.5M13 10.5h2.5" />
      </NavGlyph>
    ),
    key: (
      <NavGlyph>
        <circle cx="6" cy="12" r="3" />
        <path d="M8.2 9.8L15 3M12.5 5.5l2 2M11 7l1.5 1.5" />
      </NavGlyph>
    ),
    clock: (
      <NavGlyph>
        <circle cx="9" cy="9" r="6.5" />
        <path d="M9 5.5V9l2.5 1.5" />
      </NavGlyph>
    ),
    bookmark: (
      <NavGlyph>
        <path d="M5 2.5h8a1 1 0 011 1v12l-5-3-5 3v-12a1 1 0 011-1z" />
      </NavGlyph>
    ),
    link: (
      <NavGlyph>
        <path d="M7.5 10.5l3-3" />
        <path d="M8.5 5l1.25-1.25a2.65 2.65 0 013.75 3.75L12.25 8.75" />
        <path d="M9.5 13l-1.25 1.25a2.65 2.65 0 01-3.75-3.75L5.75 9.25" />
      </NavGlyph>
    ),
    route: (
      <NavGlyph>
        <circle cx="4.5" cy="13.5" r="1.75" />
        <circle cx="13.5" cy="4.5" r="1.75" />
        <path d="M6.25 13.5H11a2.25 2.25 0 000-4.5H7a2.25 2.25 0 010-4.5h4.75" />
      </NavGlyph>
    ),
    trend_up: (
      <NavGlyph>
        <path d="M2.5 13.5L7 9l3 3 5.5-5.5" />
        <path d="M11 6.5h4.5V11" />
      </NavGlyph>
    ),
    flag: (
      <NavGlyph>
        <path d="M4 16V2.5" />
        <path d="M4 3h9l-2 3 2 3H4" />
      </NavGlyph>
    ),
    compass: (
      <NavGlyph>
        <circle cx="9" cy="9" r="6.5" />
        <path d="M11.75 6.25l-1.5 4-4 1.5 1.5-4z" />
      </NavGlyph>
    ),
    bolt: (
      <NavGlyph>
        <path d="M10 2L4 10h4.5L8 16l6-8H9.5z" />
      </NavGlyph>
    ),
    progress: (
      <NavGlyph>
        <circle cx="9" cy="9" r="6.5" />
        <path d="M6 9.25l2 2 4-4.25" />
      </NavGlyph>
    ),
    lock: (
      <NavGlyph>
        <rect x="3.5" y="8" width="11" height="7.5" rx="1.5" />
        <path d="M6 8V5.5a3 3 0 016 0V8" />
      </NavGlyph>
    ),
    unlock: (
      <NavGlyph>
        <rect x="3.5" y="8" width="11" height="7.5" rx="1.5" />
        <path d="M6 8V5.5a3 3 0 015.6-1.5" />
      </NavGlyph>
    ),
    login: (
      <NavGlyph>
        <path d="M10.5 3H14a1 1 0 011 1v10a1 1 0 01-1 1h-3.5" />
        <path d="M3 9h8M8 6l3 3-3 3" />
      </NavGlyph>
    ),
    clipboard_check: (
      <NavGlyph>
        <rect x="4" y="3" width="10" height="13" rx="1.5" />
        <path d="M7 3V2h4v1" />
        <path d="M6.75 10l1.75 1.75L11.5 8.5" />
      </NavGlyph>
    ),
    file: (
      <NavGlyph>
        <path d="M5 2h5.5L14 5.5V15a1 1 0 01-1 1H5a1 1 0 01-1-1V3a1 1 0 011-1z" />
        <path d="M10.5 2v3.5H14" />
      </NavGlyph>
    ),
    bell: (
      <NavGlyph>
        <path d="M5 12.5V8a4 4 0 018 0v4.5l1.5 1.5h-11z" />
        <path d="M7.5 16h3" />
      </NavGlyph>
    ),
    log: (
      <NavGlyph>
        <path d="M3 4.5h1M3 9h1M3 13.5h1M6.5 4.5H15M6.5 9H15M6.5 13.5H15" />
      </NavGlyph>
    ),
    user_circle: (
      <NavGlyph>
        <circle cx="9" cy="9" r="6.5" />
        <circle cx="9" cy="7.5" r="2.25" />
        <path d="M5 13.5a4.5 4.5 0 018 0" />
      </NavGlyph>
    ),
    badge: (
      <NavGlyph>
        <circle cx="9" cy="7" r="4.5" />
        <path d="M6.5 10.75L5.5 16 9 14l3.5 2-1-5.25" />
      </NavGlyph>
    ),
    // Owner request 2026-11-03 — the Sector Overlays groups.
    sliders: (
      <NavGlyph>
        <path d="M3 5h7M13 5h2M3 13h2M8 13h7" />
        <circle cx="11.5" cy="5" r="1.5" />
        <circle cx="6.5" cy="13" r="1.5" />
      </NavGlyph>
    ),
    card: (
      <NavGlyph>
        <rect x="2" y="4" width="14" height="10" rx="1.5" />
        <path d="M2 7.5h14M5 11h3" />
      </NavGlyph>
    ),
    coin: (
      <NavGlyph>
        <circle cx="9" cy="9" r="6.5" />
        <path d="M10.75 6.5h-2.5a1.25 1.25 0 000 2.5h1.5a1.25 1.25 0 010 2.5h-2.5M9 5.25V6.5M9 11.5v1.25" />
      </NavGlyph>
    ),
    factory: (
      <NavGlyph>
        <path d="M2.5 15.5h13M2.5 15.5v-7l4 2.5v-2.5l4 2.5V3.5h3v12" />
      </NavGlyph>
    ),
    leaf: (
      <NavGlyph>
        <path d="M4 14c0-6.5 4-10.5 11-10.5 0 7-4 11-10.5 11" />
        <path d="M4 14l5-5" />
      </NavGlyph>
    ),
    truck: (
      <NavGlyph>
        <path d="M2 5h8.5v7H2zM10.5 7.5h3l2 2.5v2h-5" />
        <circle cx="5" cy="13.5" r="1.5" />
        <circle cx="12.5" cy="13.5" r="1.5" />
      </NavGlyph>
    ),
    landmark: (
      <NavGlyph>
        <path d="M2.5 7L9 3l6.5 4z" />
        <path d="M4 7.5v6M7.25 7.5v6M10.75 7.5v6M14 7.5v6M2.5 15.5h13" />
      </NavGlyph>
    ),
    monitor: (
      <NavGlyph>
        <rect x="2.5" y="3" width="13" height="9" rx="1.5" />
        <path d="M6.5 15.5h5M9 12v3.5" />
      </NavGlyph>
    ),
    book: (
      <NavGlyph>
        <path d="M9 5c-1.5-1.3-3.5-1.8-6.5-1.5V14c3-.3 5 .2 6.5 1.5 1.5-1.3 3.5-1.8 6.5-1.5V3.5C12.5 3.2 10.5 3.7 9 5zM9 5v10.5" />
      </NavGlyph>
    ),
    graduation: (
      <NavGlyph>
        <path d="M1.5 7L9 3.5 16.5 7 9 10.5z" />
        <path d="M4.5 8.5V12c1.2 1.3 2.7 2 4.5 2s3.3-.7 4.5-2V8.5M16.5 7v4" />
      </NavGlyph>
    ),
    hub: (
      <NavGlyph>
        <circle cx="9" cy="9" r="2" />
        <circle cx="3.5" cy="4" r="1.5" />
        <circle cx="14.5" cy="4" r="1.5" />
        <circle cx="3.5" cy="14" r="1.5" />
        <circle cx="14.5" cy="14" r="1.5" />
        <path d="M4.6 5.1l2.9 2.5M13.4 5.1l-2.9 2.5M4.6 12.9l2.9-2.5M13.4 12.9l-2.9-2.5" />
      </NavGlyph>
    ),
  };
  const svg = icons[name];
  if (!svg) return null;
  return (
    <span className="nav-icon" aria-hidden="true">
      {svg}
    </span>
  );
};

/**
 * Admin layout with collapsible sidebar navigation.
 * AesirX-style accordion menu with expandable sections.
 */
export function AdminLayout() {
  const { locale, setLocale, t } = useLocale();
  const { languages } = useInstalledLanguages();
  const { user, logout } = useAuth();
  const location = useLocation();
  const activeModule = useMemo(() => {
    if (location.pathname.startsWith('/admin/consent-properties')) return 'consent-properties';
    const moduleSegment = location.pathname.match(/^\/admin\/([^/]+)/)?.[1];
    return moduleSegment ?? 'dashboard';
  }, [location.pathname]);
  const { hasPermission } = usePermissionContext();
  const { isModuleEnabled } = useModuleConfig();
  // Sprint 63.43.0 (AC2) — the routes of the sector leaves no operational overlay
  // declares into. While the projection loads, after a failed read and where the
  // module is off, every sector leaf is hidden (so is every empty group, below);
  // Overlay Management is no registry view and is never in this set.
  // fix-sprint-20261102-0448 F5 — `notEntitled` is the server's own answer
  // (`403 PACKAGE_LIMIT` on the projection, which every signed-in user may
  // read): the section is hidden on it, even where the module toggle could not
  // be read and every module therefore reads as enabled.
  const {
    overlays: sectorOverlays,
    loading: sectorOverlaysLoading,
    notEntitled: sectorOverlaysNotEntitled,
  } = useSectorOverlayNavigation();
  const hiddenOverlayRoutes = useMemo(() => {
    const operational = sectorOverlays
      ? operationalOverlayViewIds(sectorOverlays)
      : new Set<string>();
    return new Set(
      SECTOR_OVERLAY_LEAF_VIEWS.filter((view) => !operational.has(view.id)).map((view) => view.to),
    );
  }, [sectorOverlays]);
  const isProduction = import.meta.env.PROD || import.meta.env.MODE === 'production';

  // Sprint 25.3.4.1: Mobile sidebar state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close on Escape key and manage focus trap
  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        hamburgerRef.current?.focus();
        return;
      }
      // Focus trap: Tab cycles within sidebar
      if (e.key === 'Tab' && sidebarRef.current) {
        const focusable = sidebarRef.current.querySelectorAll<HTMLElement>(
          'a[href], button, select, input, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Focus first item in sidebar on open
    requestAnimationFrame(() => {
      const firstLink = sidebarRef.current?.querySelector<HTMLElement>('a, button');
      firstLink?.focus();
    });

    // Auto-scroll active module into view
    requestAnimationFrame(() => {
      const active = sidebarRef.current?.querySelector<HTMLElement>('.active');
      active?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  const menuItems: MenuItem[] = [
    // Dashboard always first
    { label: t.admin.nav.dashboard, to: '/admin', icon: 'dashboard' },
    // Alphabetical order (A–Z) for all modules
    {
      label: t.admin.nav.access_accountability,
      icon: 'audit',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.access_accountability,
      children: [
        {
          label: t.admin.sidebar_groups.access_live_monitoring,
          children: [
            {
              label: t.access_accountability.nav.session_dashboard,
              to: '/admin/access-accountability/sessions',
            },
            {
              label: t.access_accountability.nav.access_log,
              to: '/admin/access-accountability/access-log',
              testId: 'access-log-tab',
            },
            {
              label: t.access_accountability.nav.anchoring_status,
              to: '/admin/access-accountability/anchoring',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.access_reviews_investigations,
          children: [
            {
              label: t.access_accountability.nav.review_campaigns,
              to: '/admin/access-accountability/review-campaigns',
            },
            {
              label: t.access_accountability.nav.justification_review,
              to: '/admin/access-accountability/justifications',
              testId: 'justification-tab',
            },
            {
              label: t.access_accountability.nav.pattern_analysis,
              to: '/admin/access-accountability/patterns',
            },
            {
              label: t.access_accountability.nav.investigations,
              to: '/admin/access-accountability/investigations',
              testId: 'investigations-tab',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.access_evidence_policies,
          children: [
            {
              label: t.access_accountability.nav.evidence_packages,
              to: '/admin/access-accountability/evidence-packages',
            },
            {
              label: t.access_accountability.nav.access_policies,
              to: '/admin/access-accountability/access-policies',
            },
          ],
        },
        {
          label: t.access_accountability.nav.configuration,
          to: '/admin/access-accountability/config',
          testId: 'config-tab',
        },
      ],
    },
    {
      label: t.admin.nav.assessments,
      icon: 'assessments',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.assessments,
      children: [
        { label: t.assessments.nav.dashboard, to: '/admin/assessments/dashboard' },
        { label: t.assessments.nav.dpias, to: '/admin/assessments/dpias' },
        {
          label: t.assessments?.dossier_filings?.nav_label ?? 'Dossier Filings',
          to: '/admin/assessments/dossier-filings',
        },
        // Sprint 44.4.0 — operator-facing materialisation health
        {
          label:
            (t.assessments as any)?.materialisation_health?.nav_label ??
            'Submission artifact health',
          to: '/admin/assessments/dossier-materialisation-health',
        },
        // Sprint 62.2.5 — cross-framework evidence packs, as LEAVES under
        // Assessments rather than a module of their own.
        //
        // That is not a layout preference. Every existing evidence-pack surface
        // in the product is a leaf under an owning module — `audit/evidence-packs`,
        // `consent-properties/evidence-packs`,
        // `access-accountability/evidence-packages` — and the PDPL pack flow has
        // no sidebar presence at all. Shipping this one as a top-level module
        // would have been the only exception.
        //
        // It also forced a claim nobody asked for: the manual's Part II.A
        // coverage check walks the sidebar tree and requires every *module* to
        // have a profile in `docs/content/modules-overview.md`, which is the
        // COMMERCIAL tiering document ("15 modules … 29 total"). A top-level
        // module would have declared a 30th sellable module and moved those
        // counts — a product-packaging decision, not an engineering one.
        //
        // Assessments is the right owner: Dossier Filings and Submission
        // artifact health, the platform's other regulator-facing filing
        // artefacts, are already its neighbours here.
        // P2-8 — these were bare English literals while the comment above
        // claimed they used "the defensive `?? 'English'` form". They did not.
        // A Vietnamese user saw English in the sidebar while every string inside
        // the pages was translated.
        //
        // The fix used `extractLocale({ en, vi })` so that no locale key was
        // added. Sprint 63.25.0 (OQ-4, F12) replaced it: this leaf and Audit
        // Trail's were both "Evidence Packs" / "Gói bằng chứng" — two products,
        // one name — so each now has its own `admin.nav` key, present in all 16
        // bundles (real EN and VI, R-57).
        {
          label: t.admin.nav.framework_evidence_packs,
          to: '/admin/framework-evidence-packs',
          requiredPermission: Permission.FRAMEWORK_EVIDENCE_PACK_READ,
        },
        // No "New evidence pack" leaf: the register's own "New evidence pack"
        // button opens the builder as a dialog over the list, so a second
        // entry here would only lead to the same builder as a separate page.
        // `/framework-evidence-packs/new` stays routed for deep links.
        // Sprint 62.3.0 — the AI system register, as LEAVES under Assessments
        // for the reason spelled out above: a new top-level module requires a
        // profile in `docs/content/modules-overview.md`, which is the
        // COMMERCIAL tiering document, and would declare a 30th sellable module
        // (G-110). Assessments is the right owner — screening, review and
        // evidence export are what its other leaves already do.
        //
        // Labels via `extractLocale`, so no locale key is added and the
        // 15-locale deep-parity test is untouched. The detail route
        // (`/:profileId`) gets no leaf, matching the evidence-pack precedent.
        {
          label: extractLocale({ en: 'AI System Register', vi: 'Sổ đăng ký hệ thống AI' }, locale),
          to: '/admin/ai-governance',
          requiredPermission: Permission.AI_GOVERNANCE_READ,
        },
        {
          label: extractLocale({ en: 'Register an AI system', vi: 'Đăng ký hệ thống AI' }, locale),
          to: '/admin/ai-governance/new',
          requiredPermission: Permission.AI_GOVERNANCE_WRITE,
        },
        // Sprint 62.48.0 wave 5 - the AI registers, one leaf each. Labels come from
        // `ai_governance_pages.nav`; each leaf requires the permission its route reads.
        {
          label: t.ai_governance_pages.nav.inventory,
          to: '/admin/ai-governance/inventory-reviews',
          requiredPermission: Permission.AI_GOVERNANCE_READ,
        },
        {
          label: t.ai_governance_pages.nav.payment,
          to: '/admin/ai-governance/payment-authorities',
          requiredPermission: Permission.AI_GOVERNANCE_AUTHORITY,
        },
        {
          label: t.ai_governance_pages.nav.interventions,
          to: '/admin/ai-governance/interventions',
          requiredPermission: Permission.AI_GOVERNANCE_READ,
        },
        {
          label: t.ai_governance_pages.nav.complaints,
          to: '/admin/ai-governance/complaints',
          requiredPermission: Permission.AI_GOVERNANCE_READ,
        },
        {
          label: t.ai_governance_pages.nav.framework,
          to: '/admin/ai-governance/framework-and-lines',
          requiredPermission: Permission.AI_GOVERNANCE_READ,
        },
        {
          label: t.ai_governance_pages.nav.board,
          to: '/admin/ai-governance/board-figures',
          requiredPermission: Permission.AI_GOVERNANCE_READ,
        },
        { label: t.assessments.nav.gap_report, to: '/admin/assessments/gap-report' },
        { label: t.assessments.nav.transfers, to: '/admin/assessments/transfers' },
        // Sprint 62.35.0 (Slice C) — the pre-collection protection assessment,
        // beside DPIAs and transfers; same DPIA read grant (work file V2).
        {
          label: t.assessments.pre_collection.nav_label,
          to: '/admin/assessments/pre-collection',
          requiredPermission: Permission.DPIA_READ,
        },
        {
          /*
           * Fix sprint 20260927-0100 F7 — owner ruling 2026-09-27: the Phase 28
           * DPO registers serve EVERY tier. They sat under Program Governance,
           * which is Standard and above, so a Light tenant's sidebar never
           * showed them while their routes stayed open. No section is named
           * for the privacy office; Assessments is the closest section every
           * tier holds, and it is already where the DPO keeps the
           * regulator-facing records these sit beside (DPIAs, dossier filings,
           * evidence packs, the AI register).
           *
           * G-110: a sub-group is not a module, and moving a leaf adds none.
           * Each leaf keeps the permission its route requires
           * (`isLinkVisible`), and the two privacy-office registers keep their
           * OWN `requiredPermission` because the backend declares two
           * permission pairs: a principal holding only `privacy_notice:read`
           * must see the notices leaf and not the appointments leaf. Their
           * routes follow `assessments` in `ROUTE_MODULE_MAP`, so the section
           * and the route agree
           * (`layouts/__tests__/sidebar-leaf-route-module-census-63.22.0.test.ts`).
           */
          label: t.admin.sidebar_groups.pg_privacy_office,
          children: [
            {
              label: t.privacy_office.appointments.nav,
              to: '/admin/privacy-office/appointments',
              requiredPermission: Permission.DPO_APPOINTMENT_READ,
            },
            {
              label: t.privacy_office.notices.nav,
              to: '/admin/privacy-office/notices',
              requiredPermission: Permission.PRIVACY_NOTICE_READ,
            },
            // Sprint 65.17.0 — ANON-01.
            { label: t.deidentification.title, to: '/admin/deidentification/runs' },
            // Sprint 65.18.0 — IRP-01.
            { label: t.continuity.title, to: '/admin/continuity/exercises' },
            // Sprint 65.19.0 — LEG-01: complaints, denunciations and the
            // matters an organisation elects to track.
            { label: t.legal_matters.title, to: '/admin/legal-matters/matters' },
            // Sprint 65.21.0 — uses of credit information for a purpose the
            // law makes consent-dependent.
            { label: t.credit_information.title, to: '/admin/credit-information/uses' },
          ],
        },
        {
          // Sprint 65.6.0 — DT-01. Moved here with the Privacy Office
          // registers (F7, owner ruling 2026-09-27). The leaf carries its own
          // `requiredPermission` so a principal who holds `training:read` and
          // nothing else does not see a leaf they cannot open.
          label: t.admin.sidebar_groups.pg_training,
          children: [
            {
              label: t.training.nav_assignments,
              to: '/admin/training',
              requiredPermission: Permission.TRAINING_READ,
            },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.audit_trail,
      icon: 'audit',
      requiredPermission: SIDEBAR_PERMISSION_MAP.audit_trail,
      moduleId: SIDEBAR_MODULE_MAP.audit_trail,
      children: [
        { label: t.admin.nav.audit_trail, to: '/admin/audit' },
        {
          label:
            (t.admin.nav as unknown as Record<string, string>).unified_audit || 'Unified Audit',
          to: '/admin/audit/unified',
        },
        {
          label:
            (t.admin.nav as unknown as Record<string, string>).evidence_packs || 'Evidence Packs',
          to: '/admin/audit/evidence-packs',
        },
        {
          label:
            (t.admin.nav as unknown as Record<string, string>).audit_periods || 'Audit Periods',
          to: '/admin/audit/periods',
        },
      ],
    },
    {
      label: t.admin.nav.classification,
      icon: 'classification',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.classification,
      children: [
        {
          label: t.admin.sidebar_groups.classification_taxonomies,
          children: [
            { label: t.classification.nav.taxonomies, to: '/admin/classification/taxonomies' },
            { label: t.classification.nav.labeling, to: '/admin/classification/labeling' },
            { label: t.classification.nav.lifecycle, to: '/admin/classification/lifecycle' },
          ],
        },
        {
          label: t.admin.sidebar_groups.classification_workflow,
          children: [
            {
              label: t.classification.review_tasks.title,
              to: '/admin/classification/review-tasks',
            },
            { label: t.classification.exceptions.title, to: '/admin/classification/exceptions' },
            { label: t.classification.nav.gaps, to: '/admin/classification/gaps' },
          ],
        },
        {
          label: t.admin.sidebar_groups.classification_insights,
          children: [
            {
              label: t.classification.enterprise_map.title,
              to: '/admin/classification/enterprise-map',
            },
            {
              label: t.classification.discovery.title,
              to: '/admin/classification/discovery-cycles',
            },
          ],
        },
        { label: t.classification.nav.exports, to: '/admin/classification/exports' },
      ],
    },
    {
      label: t.admin.nav.compliance_forms,
      icon: 'audit',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.compliance_forms,
      children: [
        { label: t.admin.nav.forms_dashboard, to: '/admin/forms' },
        { label: t.admin.nav.form_catalog, to: '/admin/forms/catalog' },
        { label: t.admin.nav.form_templates, to: '/admin/forms/templates' },
        {
          label: (t.forms as any).nav?.filing_case_links || 'Filing Case Links',
          to: '/admin/forms/filing-case-links',
        },
        {
          label: (t.forms as any).nav?.section_routing || 'Section Routing',
          to: '/admin/forms/section-routing',
        },
        {
          label: (t.forms as any).nav?.submission_packages || 'Submission Packages',
          to: '/admin/forms/submission-packages',
        },
      ],
    },
    {
      label: t.admin.nav.connectors,
      icon: 'connectors',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.connectors,
      children: [
        {
          label: t.admin.sidebar_groups.connectors_runtime,
          children: [
            { label: t.connectors.nav.instances, to: '/admin/connectors/instances' },
            { label: t.connectors.nav.credentials, to: '/admin/connectors/credentials' },
            { label: t.connectors.nav.event_bus, to: '/admin/connectors/event-bus' },
            { label: t.connectors.nav.definitions, to: '/admin/connectors/definitions' },
          ],
        },
        {
          label: t.admin.sidebar_groups.connectors_due_diligence,
          children: [
            {
              label: t.connectors.nav.security_reviews,
              to: '/admin/connectors/security-reviews',
            },
            { label: t.connectors.nav.poc_scorecards, to: '/admin/connectors/poc-scorecards' },
            { label: t.connectors.nav.brds, to: '/admin/connectors/brds' },
            {
              label: t.connectors.nav.automation_planning,
              to: '/admin/connectors/automation-planning',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.connectors_coverage,
          children: [
            { label: t.connectors.nav.system_inventory, to: '/admin/connectors/system-inventory' },
            {
              label: t.connectors.nav.traceability_matrix,
              to: '/admin/connectors/traceability-matrix',
            },
            { label: t.connectors.nav.gap_register, to: '/admin/connectors/gap-register' },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.consent,
      icon: 'consent',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.consent,
      children: [
        {
          label: t.admin.sidebar_groups.consent_cmp_webhooks,
          children: [
            { label: t.admin.nav.cmp_integration, to: '/admin/consent/cmp-integration' },
            { label: t.admin.nav.bulk_sync, to: '/admin/consent/cmp-integration/remap' },
            {
              label: t.admin.nav.purpose_mapping,
              to: '/admin/consent/cmp-integration/purpose-mapping',
            },
            { label: t.admin.nav.webhook_config, to: '/admin/consent/cmp-integration/webhook' },
            { label: t.consent.nav.snapshots, to: '/admin/consent/snapshots' },
            { label: t.consent.sync.title, to: '/admin/consent/sync' },
            { label: t.consent.reconciliation.title, to: '/admin/consent/reconciliation' },
            { label: t.consent.nav.change_awareness, to: '/admin/consent/change-awareness' },
          ],
        },
        { label: t.consent.nav.audit, to: '/admin/consent/audit' },
        { label: t.consent.nav.policies, to: '/admin/consent/policies' },
        { label: t.consent.nav.purposes, to: '/admin/consent/purposes' },
        // Sprint 48.1.4 — Forseti consent gap analysis
        {
          label: t.consent?.gap_analysis?.nav_label ?? 'Gap Analysis',
          to: '/admin/consent/gap-analysis',
        },
        { label: t.consent.nav.workflows, to: '/admin/consent/workflows' },
        {
          label: t.consent?.import?.nav_title ?? 'Bulk Import',
          to: '/admin/consent/workflows/import',
        },
        { label: t.consent.propagation.title, to: '/admin/consent/withdrawal-propagation' },
        // Sprint 62.24.0 — consent evidence by recipient class (AC2/AC3).
        { label: t.consent.nav.evidence, to: '/admin/consent/evidence' },
        // Sprint 62.28.0 — marketing-communications consent, suppression and
        // suppression-enforcement evidence. A SUB-GROUP on the consent module,
        // not a module of its own (G-110): the records are consent records, and
        // a new module would need a tier ruling these leaves do not need.
        //
        // fix-sprint-20260915-2128 Finding 20 — each leaf declares its OWN
        // grant, like every sibling register (`Sector Licences`,
        // `Credit-Information Supply Register`, …).
        //
        // The three leaves shipped with none. This group's gate is
        // `SIDEBAR_PERMISSION_MAP.consent` (`consent_policy:read`), but these
        // routes leave the consent prefix: `route-permissions.ts` gives
        // `marketing-communications` its own row on
        // `MARKETING_COMMUNICATIONS_READ`, deliberately, "so the route guard
        // checks the grant the routes themselves check". `seed-roles.ts`
        // records that `marketing_communications:read` reaches only the auditor
        // by suffix and sits in no tier bucket, so a role holding consent-policy
        // read WITHOUT the marketing grant is an ordinary configuration — and
        // it was shown three menu items that all land on Access Denied.
        {
          label: t.admin.sidebar_groups.marketing_communications,
          children: [
            {
              label: t.consent.nav.marketing_consent,
              to: '/admin/marketing-communications/consents',
              requiredPermission: Permission.MARKETING_COMMUNICATIONS_READ,
            },
            {
              label: t.consent.nav.marketing_suppression,
              to: '/admin/marketing-communications/suppressions',
              requiredPermission: Permission.MARKETING_COMMUNICATIONS_READ,
            },
            {
              label: t.consent.nav.marketing_suppression_enforcement,
              to: '/admin/marketing-communications/enforcement',
              requiredPermission: Permission.MARKETING_COMMUNICATIONS_READ,
            },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.consent_properties,
      icon: 'consent',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.consent_properties,
      children: [
        { label: t.consent_properties.nav.dashboard, to: '/admin/consent-properties/dashboard' },
        { label: t.consent_properties.analytics.title, to: '/admin/consent-properties/analytics' },
        {
          label: t.admin.sidebar_groups.cp_definitions,
          children: [
            {
              label: t.consent_properties.nav.properties,
              to: '/admin/consent-properties/properties',
              testId: 'properties-tab',
            },
            {
              label: t.consent_properties.nav.groups,
              to: '/admin/consent-properties/groups',
              testId: 'groups-tab',
            },
            {
              label: t.consent_properties.nav.golden_templates,
              to: '/admin/consent-properties/golden-templates',
              testId: 'golden-templates-tab',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.cp_lifecycle,
          children: [
            {
              label: t.consent_properties.nav.bindings,
              to: '/admin/consent-properties/bindings',
              testId: 'bindings-tab',
            },
            {
              label: t.consent_properties.nav.change_requests,
              to: '/admin/consent-properties/change-requests',
            },
            {
              label: t.consent_properties.rollouts.title,
              to: '/admin/consent-properties/rollouts',
            },
            {
              label: t.consent_properties.nav.snapshot_schedules,
              to: '/admin/consent-properties/snapshot-schedules',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.cp_quality,
          children: [
            {
              label: t.consent_properties.nav.comparison,
              to: '/admin/consent-properties/comparison',
              testId: 'comparison-tab',
            },
            {
              label: t.consent_properties.nav.inconsistencies,
              to: '/admin/consent-properties/inconsistencies',
            },
            {
              label: t.consent_properties.nav.evidence_packs,
              to: '/admin/consent-properties/evidence-packs',
            },
          ],
        },
        { label: t.consent_properties.nav.audit, to: '/admin/consent-properties/audit' },
      ],
    },
    {
      label: (t as any).data_subjects?.nav_title ?? 'Data Subjects',
      to: '/admin/data-subjects',
      icon: 'people',
      requiredPermission: SIDEBAR_PERMISSION_MAP.data_subjects,
      moduleId: SIDEBAR_MODULE_MAP.data_subjects,
      children: [
        {
          label: (t as any).data_subjects?.nav?.profiles ?? 'Profiles',
          to: '/admin/data-subjects',
        },
      ],
    },
    {
      label: t.admin.nav.data_mapping,
      icon: 'data',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.data_mapping,
      children: [
        { label: t.data_mapping.nav.flows, to: '/admin/data-mapping/flows' },
        {
          label: t.admin.sidebar_groups.data_mapping_ropa,
          children: [
            { label: t.data_mapping.nav.systems, to: '/admin/data-mapping/systems' },
            { label: t.data_mapping.nav.activities, to: '/admin/data-mapping/activities' },
            {
              label: t.data_mapping.nav.ropa_completeness,
              to: '/admin/data-mapping/ropa-completeness',
            },
            { label: t.data_mapping.nav.categories, to: '/admin/data-mapping/categories' },
            // Sprint 45.4.0 — Data Registry between Categories and Flows.
            { label: t.data_mapping.nav.registry, to: '/admin/data-mapping/registry' },
            { label: t.data_mapping.nav.purposes, to: '/admin/data-mapping/purposes' },
            { label: t.data_mapping.nav.retention, to: '/admin/data-mapping/retention' },
          ],
        },
        {
          label: t.admin.sidebar_groups.data_mapping_classification,
          children: [
            { label: t.data_mapping.nav.review, to: '/admin/data-mapping/review' },
            { label: t.data_mapping.nav.inventory, to: '/admin/data-mapping/inventory' },
            { label: t.data_mapping.nav.exceptions, to: '/admin/data-mapping/exceptions' },
            { label: t.data_mapping.nav.history, to: '/admin/data-mapping/history' },
            // Sprint 62.4.0 — regulatory classification, as LEAVES under Data
            // Mapping's existing Classification group. A new top-level module
            // would require a profile in `docs/content/modules-overview.md`,
            // which is the COMMERCIAL tiering document, and would declare a new
            // sellable module (G-110). Data Mapping is the right owner: these
            // subjects *are* its registry items and its systems, seen from the
            // regulator's side.
            //
            // Labels via `extractLocale`, so no locale key is added and the
            // 15-locale deep-parity test is untouched. The detail, form,
            // screening and offering-edit routes get no leaf, matching the
            // evidence-pack and AI-register precedents.
            {
              label: extractLocale(
                { en: 'Subject Register', vi: 'Sổ đăng ký đối tượng phân loại' },
                locale,
              ),
              to: '/admin/regulatory-classification',
              requiredPermission: Permission.REGULATORY_CLASSIFICATION_READ,
            },
            {
              label: extractLocale(
                { en: 'Regulated Offerings', vi: 'Sản phẩm, dịch vụ được quản lý' },
                locale,
              ),
              to: '/admin/regulatory-classification/offerings',
              requiredPermission: Permission.REGULATORY_CLASSIFICATION_READ,
            },
          ],
        },
        // ── Sprint 62.8.0 — industrial OT assurance ─────────────
        //
        // Two leaves, and they are in **this** module rather than beside the
        // other seven manufacturing leaves under Program Governance, because the
        // locked architecture decision reads *"OT extends canonical systems; it
        // does not create a disconnected asset inventory"* — and `grc_systems`
        // with its CRUD lives here. `OtAssetProfile.system_id` is non-nullable,
        // so the extension is structural; putting the navigation somewhere else
        // would hide that and invite exactly the parallel inventory the decision
        // forbids.
        //
        // The tier consequence is deliberate and is recorded in the work file:
        // Data Mapping is a **Light-tier** module, so a Light tenant running a
        // factory can record its OT assets, and the OT half of the help content
        // is authored to Light-tier voice.
        {
          label: extractLocale(
            { en: 'Industrial OT Assurance', vi: 'Bảo đảm an ninh OT công nghiệp' },
            locale,
          ),
          children: [
            {
              label: extractLocale({ en: 'OT Asset Profiles', vi: 'Hồ sơ tài sản OT' }, locale),
              to: '/admin/manufacturing/ot-assets',
              requiredPermission: Permission.MANUFACTURING_OT_READ,
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.data_mapping_governance,
          children: [
            { label: t.data_mapping.nav.governance, to: '/admin/data-mapping/governance' },
            { label: t.data_mapping.nav.attestation, to: '/admin/data-mapping/attestation' },
            { label: t.data_mapping.nav.evidence, to: '/admin/data-mapping/evidence' },
          ],
        },
        { label: t.data_mapping.nav.exports, to: '/admin/data-mapping/exports' },
      ],
    },
    {
      label: t.admin.nav.deletion_orchestration,
      icon: 'deletion',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.deletion_orchestration,
      children: [
        {
          label: t.deletion_orchestration.nav.dashboard,
          to: '/admin/deletion-orchestration/dashboard',
        },
        {
          label: t.admin.sidebar_groups.deletion_execution,
          children: [
            {
              label: t.deletion_orchestration.nav.runs,
              to: '/admin/deletion-orchestration/runs',
            },
            {
              label: t.deletion_orchestration.nav.tasks,
              to: '/admin/deletion-orchestration/tasks',
            },
            {
              label: t.deletion_orchestration.nav.templates,
              to: '/admin/deletion-orchestration/templates',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.deletion_vendors,
          children: [
            {
              label: t.deletion_orchestration.nav.vendor_requests,
              to: '/admin/deletion-orchestration/vendor-requests',
            },
            {
              label: t.deletion_orchestration.nav.vendor_verification,
              to: '/admin/deletion-orchestration/vendor-verification',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.deletion_evidence,
          children: [
            {
              label: t.deletion_orchestration.nav.proof_chain,
              to: '/admin/deletion-orchestration/proof-chain',
            },
            {
              label: t.deletion_orchestration.nav.completeness,
              to: '/admin/deletion-orchestration/completeness',
            },
            {
              label: t.deletion_orchestration.nav.dsr_integration,
              to: '/admin/deletion-orchestration/dsr-integration',
            },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.discovery,
      icon: 'discovery',
      // Sprint 63.29.0 (#572) — no section gate. Classification Rules opens on
      // `classification_rule:read`, the grant its routes ask, and the other leaves on
      // `discovery:read` through the module prefix (`route-permissions.ts`); a section
      // gate on `discovery:read` would hide a rules-only role from its one page. Each
      // link shows when its route opens (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.discovery,
      children: [
        { label: t.discovery.nav.dashboard, to: '/admin/discovery/dashboard' },
        {
          label: t.admin.sidebar_groups.discovery_rules,
          children: [
            {
              label: t.discovery.nav.classification_rules,
              to: '/admin/discovery/classification-rules',
            },
            { label: t.discovery.nav.dedup_rules, to: '/admin/discovery/dedup-rules' },
            { label: t.discovery.nav.import_templates, to: '/admin/discovery/import-templates' },
          ],
        },
        {
          label: t.admin.sidebar_groups.discovery_scanning,
          children: [
            { label: t.discovery.nav.connectors, to: '/admin/discovery/connectors' },
            { label: t.discovery.nav.scans, to: '/admin/discovery/scans' },
            { label: t.discovery.nav.discovery_cycles, to: '/admin/discovery/cycles' },
          ],
        },
        {
          label: t.admin.sidebar_groups.discovery_review,
          children: [
            { label: t.discovery.nav.staging_area, to: '/admin/discovery/staging' },
            { label: t.discovery.nav.suggestions, to: '/admin/discovery/suggestions' },
            { label: t.discovery.nav.owner_review, to: '/admin/discovery/owner-review' },
          ],
        },
        {
          label: t.admin.sidebar_groups.discovery_insights,
          children: [
            { label: t.discovery.nav.analytics, to: '/admin/discovery/analytics' },
            {
              label: t.discovery.nav.classification_lineage,
              to: '/admin/discovery/lineage',
            },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.governance,
      icon: 'governance',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.governance,
      children: [
        {
          label: t.admin.sidebar_groups.governance_oversight,
          children: [
            { label: t.governance.nav.approvals, to: '/admin/governance/approvals' },
            // Sprint 41.2.0 E6 — Governance Audit Trail
            { label: t.governance.nav.audit_trail, to: '/admin/governance/audit-trail' },
            { label: t.governance.nav.drift, to: '/admin/governance/drift' },
            // Sprint 65.1.0 — the DPO work programme (B0: Compliance Work
            // Programme / Chương trình công việc tuân thủ). Oversight is the
            // right group: it is the aggregate read of which duties are
            // running, on whose clock, and what evidence is missing.
            //
            // Gated on its OWN read permission, never on the Governance
            // group's — a leaf that inherits the group's grant is a leaf a role
            // can see without holding the permission its page enforces. Its
            // detail route gets no leaf, per the standing detail-page
            // precedent. Labels via `extractLocale`, so no key is added to the
            // 16 locale bundles (R-57).
            {
              label: extractLocale(
                { en: 'Compliance Work Programme', vi: 'Chương trình công việc tuân thủ' },
                locale,
              ),
              to: '/admin/compliance-operations',
              requiredPermission: Permission.COMPLIANCE_OBLIGATION_READ,
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.governance_reporting,
          children: [
            { label: t.governance.nav.board_report, to: '/admin/governance/board-report' },
            { label: t.governance.nav.scores, to: '/admin/governance/scores' },
          ],
        },
        {
          label: t.admin.sidebar_groups.governance_vendors,
          children: [
            { label: t.governance.nav.vendors, to: '/admin/governance/vendors' },
            { label: t.governance.nav.subprocessors, to: '/admin/governance/subprocessors' },
            { label: t.governance.nav.cross_border, to: '/admin/governance/cross-border' },
            // Sprint 41.5.0 G4 — onboarding/dpa_reviews removed; canonical lifecycles
            // now live on vendor-risk/VendorOnboardingCasePage. Legacy paths redirect
            // via DeprecatedGovernanceRedirect for bookmarked users.
            { label: t.governance.nav.offboarding, to: '/admin/governance/offboarding' },
          ],
        },
        {
          label: t.admin.sidebar_groups.governance_lifecycle,
          children: [
            {
              label: t.governance.nav.periodic_reviews,
              to: '/admin/governance/periodic-reviews',
            },
            { label: t.governance.nav.playbooks, to: '/admin/governance/playbooks' },
            { label: t.governance.nav.evidence, to: '/admin/governance/evidence' },
          ],
        },
        {
          // Sprint 62.5.0 — identity, trust service and electronic transaction
          // evidence, as LEAVES under Governance rather than a new top-level
          // module. A new module would require a profile in
          // `docs/content/modules-overview.md`, which is the COMMERCIAL tiering
          // document, and would declare a 30th sellable module — a product
          // decision arriving as a side effect of a sidebar edit (G-110). The
          // counts stay at Light 15 / Standard 26 / Enterprise 29.
          //
          // Governance is the right owner: these records are the organisation's
          // evidence about the identities it relies on, the third parties it
          // trusts, and what it has filed. That is oversight, and it sits beside
          // the vendor and evidence groups already here.
          //
          // Labels via `extractLocale`, so no locale key is added and the
          // 15-locale deep-parity test is untouched. Detail routes get no leaf,
          // matching the evidence-pack, AI-register and classification
          // precedents.
          //
          // The Vietnamese strings must match the help article and
          // `modules-overview.vi.md` exactly — a surface with two names is a
          // surface a user cannot find. **This comment used to assert that they
          // did**, and gate E12.5 found that Electronic Agreements was
          // `Hợp đồng điện tử` here and `Thỏa thuận điện tử` in both documents.
          // Not a typo: `hợp đồng` is *contract* and `thỏa thuận` is
          // *agreement*, which are distinct legal registers, and the record
          // stores a pack-supplied generic `agreement_type_code` — so naming it
          // "contract" narrowed a generic register to one of its kinds.
          //
          // The claim was checkable and unchecked, which is the whole failure:
          // `strings-terminology.test.ts` pinned the Vietnamese names against
          // `strings.ts` — the file they came from — so it compared a source to
          // itself and could never have seen this. It now pins them against the
          // help article JSON and `modules-overview.vi.md` as well.
          label: extractLocale(
            { en: 'Trust & Identity Evidence', vi: 'Bằng chứng danh tính và dịch vụ tin cậy' },
            locale,
          ),
          children: [
            {
              label: extractLocale({ en: 'Identity Register', vi: 'Sổ đăng ký danh tính' }, locale),
              to: '/admin/trust-evidence/identities',
              requiredPermission: Permission.TRUST_EVIDENCE_READ,
            },
            {
              label: extractLocale(
                { en: 'Trust Providers', vi: 'Tổ chức cung cấp dịch vụ tin cậy' },
                locale,
              ),
              to: '/admin/trust-evidence/providers',
              requiredPermission: Permission.TRUST_EVIDENCE_READ,
            },
            {
              label: extractLocale(
                { en: 'Electronic Submissions', vi: 'Hồ sơ nộp điện tử' },
                locale,
              ),
              to: '/admin/trust-evidence/submissions',
              requiredPermission: Permission.TRUST_EVIDENCE_READ,
            },
            {
              label: extractLocale(
                { en: 'Electronic Agreements', vi: 'Thỏa thuận điện tử' },
                locale,
              ),
              to: '/admin/trust-evidence/agreements',
              requiredPermission: Permission.TRUST_EVIDENCE_READ,
            },
            {
              // Sprint 62.39.0 — elective. A relying party's record of a
              // cross-border identity exchange and the gate before production.
              // The platform connects to no identity system.
              label: extractLocale(
                { en: 'Cross-Border Identity Exchanges', vi: 'Trao đổi định danh xuyên biên giới' },
                locale,
              ),
              to: '/admin/trust-evidence/identity-exchanges',
              requiredPermission: Permission.TRUST_EVIDENCE_READ,
            },
          ],
        },
        {
          // Sprint 62.6.0 — what a regulatory artifact IS, and who may see it,
          // as LEAVES under Governance rather than a new top-level module. The
          // reasoning is 62.5.0's and it matters more here: a new module would
          // require a profile in `docs/content/modules-overview.md`, which is
          // the COMMERCIAL tiering document, and would declare a 30th sellable
          // module — a product decision arriving as a side effect of a sidebar
          // edit (G-110). The counts stay at Light 15 / Standard 26 /
          // Enterprise 29.
          //
          // And putting an access control behind an upsell would sell the
          // smallest customers a product that cannot protect them: an
          // organisation that handles classified registers carries that duty
          // whether it is large or small.
          //
          // Governance is the right owner. Classifying what the organisation
          // holds, and controlling who may reach it, is oversight — it sits
          // beside the vendor, evidence and trust groups already here.
          //
          // Labels via `extractLocale`, so no locale key is added and the
          // 15-locale deep-parity test is untouched. The detail route gets no
          // leaf, matching the evidence-pack, AI-register, classification and
          // trust-evidence precedents.
          //
          // The Vietnamese is **Tài liệu pháp lý**, not the *Hiện vật pháp lý*
          // an earlier draft carried: `hiện vật` is a physical relic or museum
          // object and these are documents. The three leaf names below are the
          // same strings the help article and `modules-overview.vi.md` use, and
          // `pages/admin/regulatory-artifacts/__tests__/strings-terminology.test.ts`
          // pins them against **both documents** rather than against the file
          // they came from — which is the guard 62.5.0 was missing when the
          // product said `Hợp đồng điện tử` and both documents said
          // `Thỏa thuận điện tử`.
          label: extractLocale({ en: 'Regulatory Artifacts', vi: 'Tài liệu pháp lý' }, locale),
          children: [
            {
              label: extractLocale({ en: 'Artifact Catalog', vi: 'Danh mục tài liệu' }, locale),
              to: '/admin/regulatory-artifacts',
              requiredPermission: Permission.REGULATORY_ARTIFACT_READ,
            },
            {
              label: extractLocale(
                { en: 'Restricted Access', vi: 'Quyền truy cập hạn chế' },
                locale,
              ),
              to: '/admin/regulatory-artifacts/grants',
              // `:custody`, not `:read`. Who holds standing access to which
              // restricted record is itself a map of what the organisation
              // restricts, so the leaf is withheld from the read-only auditor
              // exactly as both routes behind it are.
              requiredPermission: Permission.REGULATORY_ARTIFACT_CUSTODY,
            },
            {
              label: extractLocale({ en: 'Verification Queue', vi: 'Hàng đợi xác minh' }, locale),
              to: '/admin/regulatory-artifacts/verification',
              requiredPermission: Permission.REGULATORY_ARTIFACT_READ,
            },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.incident_ops,
      icon: 'incidents',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.incident_ops,
      children: [
        {
          label: t.admin.sidebar_groups.incident_ops_dashboards,
          children: [
            { label: t.incident_ops.nav.sla_dashboard, to: '/admin/incident-ops/sla-dashboard' },
            {
              label: t.incident_ops.nav.coordination_dashboard,
              to: '/admin/incident-ops/coordination-dashboard',
            },
            { label: t.incident_ops.nav.metrics, to: '/admin/incident-ops/metrics' },
          ],
        },
        {
          label: t.admin.sidebar_groups.incident_ops_sla,
          children: [
            {
              label: t.incident_ops.nav.sla_definitions,
              to: '/admin/incident-ops/sla-definitions',
            },
            {
              label: t.incident_ops.nav.regulatory_sla,
              to: '/admin/incident-ops/regulatory-sla',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.incident_ops_playbooks,
          children: [
            { label: t.incident_ops.nav.playbooks, to: '/admin/incident-ops/playbooks' },
            { label: t.incident_ops.nav.playbook_links, to: '/admin/incident-ops/playbook-links' },
          ],
        },
        {
          label: t.admin.sidebar_groups.incident_ops_vendor_regulator,
          children: [
            {
              label: t.incident_ops.nav.vendor_coordination,
              to: '/admin/incident-ops/vendor-coordination',
            },
            {
              label: t.incident_ops.nav.regulator_exports,
              to: '/admin/incident-ops/regulator-exports',
            },
            { label: t.incident_ops.nav.escalation_log, to: '/admin/incident-ops/escalations' },
          ],
        },
        {
          label: t.admin.sidebar_groups.incident_ops_evidence,
          children: [
            { label: t.incident_ops.nav.evidence_chain, to: '/admin/incident-ops/evidence-chain' },
            { label: t.incident_ops.nav.post_review, to: '/admin/incident-ops/post-review' },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.incidents,
      icon: 'incidents',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.incidents,
      children: [
        {
          label: t.admin.sidebar_groups.incidents_workload,
          children: [
            { label: t.incidents.nav.all, to: '/admin/incidents' },
            { label: t.incidents.nav.tasks, to: '/admin/incidents/tasks' },
            { label: t.incidents.nav.remediation, to: '/admin/incidents/remediation' },
          ],
        },
        {
          label: t.admin.sidebar_groups.incidents_regulatory,
          children: [
            {
              label: t.incidents.nav.breach_notifications,
              to: '/admin/incidents/breach-notifications',
            },
            {
              label: t.incidents.nav.authority_requests,
              to: '/admin/incidents/authority-requests',
            },
            {
              label: t.incidents.nav.disclosure_register,
              to: '/admin/incidents/disclosure-register',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.incidents_reporting,
          children: [
            { label: t.incidents.nav.reports, to: '/admin/incidents/reports' },
            { label: t.incidents.nav.supplements, to: '/admin/incidents/supplements' },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.localization,
      icon: 'globe',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.localization,
      children: [
        {
          label: t.admin.sidebar_groups.localization_content,
          children: [
            { label: t.localization.nav.translations, to: '/admin/localization/translations' },
            { label: t.localization.nav.language_packs, to: '/admin/localization/language-packs' },
            { label: t.localization.nav.market_packs, to: '/admin/localization/market-packs' },
            {
              // fix-sprint-20260905-2135 F35 — the last hardcoded English leaf
              // in this group. Every sibling reads a translated key, and the
              // key it needs already exists in all 16 locales, so a Vietnamese
              // operator had a fully Vietnamese sidebar with one English leaf
              // opening a page titled 'Gói nội dung trợ giúp'.
              label: t.localization.help_content_packs.page_title,
              to: '/admin/localization/help-content-packs',
            },
            { label: t.localization.nav.mappings, to: '/admin/localization/mappings' },
          ],
        },
        {
          label: t.admin.sidebar_groups.localization_observability,
          children: [
            { label: t.localization.nav.dashboard, to: '/admin/localization/dashboard' },
            { label: t.localization.nav.audit, to: '/admin/localization/audit' },
          ],
        },
      ],
    },
    // Sprint 63.42.2 (owner request 2026-11-01, AC3) — Regulatory, a second
    // menu section of the Localization module (same module id, package and
    // permissions; no new sellable module, G-110): the framework catalogue and
    // the framework-agnostic regulatory registers, none of which is
    // localisation. Its first leaf is a Localization URL, so its category and
    // help slug are declared here rather than derived from that path. Leaves
    // keep their URLs, permissions and order; Module Tips ids keep their
    // `localization_*` values so no dismissal resets.
    {
      label: t.admin.nav.regulatory,
      icon: 'regulatory',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.localization,
      category: 'operations_automation',
      helpSlug: 'localization',
      children: [
        // The former Localization › Regulatory group, renamed so the menu never
        // reads Regulatory › Regulatory.
        {
          label: t.admin.sidebar_groups.regulatory_frameworks,
          children: [
            { label: t.localization.nav.frameworks, to: '/admin/localization/frameworks' },
            {
              label:
                (t.admin.nav as unknown as Record<string, string>).regulatory_updates ||
                'Regulatory Updates',
              to: '/admin/localization/regulatory-updates',
            },
            { label: t.localization.nav.submissions, to: '/admin/localization/submissions' },
          ],
        },
        // Sprint 63.43.0 (owner ruling OR-26, plan §3.2) — the old Localization ›
        // Sector Overlays group is gone. Its shared and baseline registers, and legal
        // holds (consulted by the core destruction gates, so never entitlement-gated),
        // sit here; the screens that read every installed fine area sit in Penalties &
        // Enforcement below; its overlay-exclusive registers moved to the Sector
        // Overlays section. Each leaf keeps its label and its own read permission.
        {
          label: t.admin.sidebar_groups.localization_regulatory_registers,
          children: [
            {
              label: extractLocale(
                { en: 'Authority Directions', vi: 'Yêu cầu của cơ quan có thẩm quyền' },
                locale,
              ),
              to: '/admin/authority-directions',
              requiredPermission: Permission.AUTHORITY_DIRECTION_READ,
            },
            {
              label: extractLocale({ en: 'Sector Licences', vi: 'Giấy phép ngành' }, locale),
              to: '/admin/sector-licences',
              requiredPermission: Permission.SECTOR_LICENCE_READ,
            },
            {
              label: extractLocale({ en: 'Marketing Approvals', vi: 'Phê duyệt tiếp thị' }, locale),
              to: '/admin/marketing-approvals',
              requiredPermission: Permission.MARKETING_APPROVAL_READ,
            },
            {
              label: extractLocale(
                { en: 'Regulatory Disclosures', vi: 'Công bố thông tin' },
                locale,
              ),
              to: '/admin/regulatory-disclosures',
              requiredPermission: Permission.REGULATORY_DISCLOSURE_READ,
            },
            // Sprint 63.24.0 Slice A — the controlled-testing case register. Shared
            // (plan §2: a baseline pack's procedures open cases too), so it stays
            // here (Regulatory since 63.42.2) and is linked from the Sector Overlays
            // Overview.
            {
              label: extractLocale(
                { en: 'Controlled-Testing Cases', vi: 'Hồ sơ thử nghiệm có kiểm soát' },
                locale,
              ),
              to: '/admin/controlled-testing/cases',
              requiredPermission: Permission.CONTROLLED_TESTING_CASE_READ,
            },
            {
              label: extractLocale(
                { en: 'Data-Service Provider Records', vi: 'Hồ sơ nhà cung cấp dịch vụ dữ liệu' },
                locale,
              ),
              to: '/admin/data-service-providers',
              requiredPermission: Permission.DATA_SERVICE_PROVIDER_READ,
            },
            {
              label: extractLocale(
                { en: 'Product Classifications', vi: 'Phân loại sản phẩm' },
                locale,
              ),
              to: '/admin/product-classifications',
              requiredPermission: Permission.REGULATORY_CLASSIFICATION_READ,
            },
            {
              label: extractLocale(
                { en: 'Conformity Certificates', vi: 'Giấy chứng nhận hợp quy' },
                locale,
              ),
              to: '/admin/conformity-certificates',
              requiredPermission: Permission.REGULATORY_CLASSIFICATION_READ,
            },
            {
              label: extractLocale(
                { en: 'Regulatory Placeholders', vi: 'Nội dung dự liệu theo quy định' },
                locale,
              ),
              to: '/admin/regulatory-placeholders',
              requiredPermission: Permission.REGULATORY_PLACEHOLDERS_READ,
            },
            {
              label: extractLocale({ en: 'Legal Holds', vi: 'Lệnh phong tỏa pháp lý' }, locale),
              to: '/admin/legal-holds',
              requiredPermission: Permission.LEGAL_HOLD_READ,
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.localization_penalties_enforcement,
          children: [
            // Sprint 63.43.0 — the screens that read every installed framework's
            // fine areas, from the old Sector Overlays group in their existing
            // order. Each keeps its own read permission.
            {
              label: extractLocale(
                { en: 'Penalty Readiness', vi: 'Mức độ sẵn sàng trước chế tài' },
                locale,
              ),
              to: '/admin/penalty-exposure',
              requiredPermission: Permission.PENALTY_EXPOSURE_READ,
            },
            {
              label: extractLocale({ en: 'Exposure Inputs', vi: 'Dữ kiện tính mức phạt' }, locale),
              to: '/admin/penalty-exposure/inputs',
              requiredPermission: Permission.PENALTY_EXPOSURE_READ,
            },
            // Sprint 62.35.0 (Slice A) — the enforcement register.
            {
              label: extractLocale(
                { en: 'Enforcement Register', vi: 'Sổ theo dõi chế tài' },
                locale,
              ),
              to: '/admin/penalty-exposure/register',
              requiredPermission: Permission.PENALTY_EXPOSURE_READ,
            },
            {
              label: extractLocale({ en: 'Enforcement Cases', vi: 'Vụ việc xử phạt' }, locale),
              to: '/admin/enforcement-cases',
              requiredPermission: Permission.ENFORCEMENT_CASE_READ,
            },
          ],
        },
        // Sprint 62.51.0 — the data-exploitation registers (D6: a sub-group;
        // sub-groups do not nest; under Regulatory since 63.42.2). Every leaf on
        // the module's read grant; names from the R-44 batch
        // 2026-10-24-62.51.0-data-exploitation-leaves.
        {
          label: t.admin.sidebar_groups.localization_data_exploitation,
          children: [
            {
              label: extractLocale({ en: 'Exploitation Routes', vi: 'Cơ chế khai thác' }, locale),
              to: '/admin/data-exploitation/routes',
              requiredPermission: Permission.DATA_EXPLOITATION_READ,
            },
            {
              label: extractLocale(
                { en: 'Requests and Grants', vi: 'Yêu cầu và kết quả giải quyết' },
                locale,
              ),
              to: '/admin/data-exploitation/requests',
              requiredPermission: Permission.DATA_EXPLOITATION_READ,
            },
            {
              label: extractLocale(
                { en: 'Controlled Testing Records', vi: 'Bản ghi thử nghiệm có kiểm soát' },
                locale,
              ),
              to: '/admin/data-exploitation/testing',
              requiredPermission: Permission.DATA_EXPLOITATION_READ,
            },
            {
              label: extractLocale(
                { en: 'Incident Notices', vi: 'Thông báo sự cố, vi phạm' },
                locale,
              ),
              to: '/admin/data-exploitation/notices',
              requiredPermission: Permission.DATA_EXPLOITATION_READ,
            },
            {
              label: extractLocale(
                { en: 'End-of-Purpose Disposition', vi: 'Hoàn trả, xóa, tiêu hủy dữ liệu' },
                locale,
              ),
              to: '/admin/data-exploitation/dispositions',
              requiredPermission: Permission.DATA_EXPLOITATION_READ,
            },
            {
              label: extractLocale({ en: 'Incubator Records', vi: 'Ươm tạo dữ liệu' }, locale),
              to: '/admin/data-exploitation/incubator',
              requiredPermission: Permission.DATA_EXPLOITATION_READ,
            },
            {
              label: extractLocale(
                {
                  en: 'Foreign Cooperation Assessments',
                  vi: 'Đánh giá rủi ro hợp tác với bên nước ngoài',
                },
                locale,
              ),
              to: '/admin/data-exploitation/foreign-cooperation',
              requiredPermission: Permission.DATA_EXPLOITATION_READ,
            },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.monitoring,
      icon: 'monitoring',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.monitoring,
      children: [
        {
          label: t.admin.sidebar_groups.monitoring_metrics,
          children: [
            { label: t.monitoring.nav.kpi, to: '/admin/monitoring/kpi' },
            {
              label: t.monitoring.nav.authority_readiness,
              to: '/admin/monitoring/authority-readiness',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.monitoring_programs,
          children: [
            { label: t.monitoring.nav.programs, to: '/admin/monitoring/programs' },
            { label: t.monitoring.nav.periods, to: '/admin/monitoring/periods' },
            { label: t.monitoring.nav.framework_link, to: '/admin/monitoring/framework-link' },
          ],
        },
        {
          label: t.admin.sidebar_groups.monitoring_tasks,
          children: [
            { label: t.monitoring.nav.tasks, to: '/admin/monitoring/tasks' },
            { label: t.monitoring.nav.task_templates, to: '/admin/monitoring/task-templates' },
            { label: t.monitoring.nav.raci, to: '/admin/monitoring/raci' },
          ],
        },
        {
          label: t.admin.sidebar_groups.monitoring_controls,
          children: [
            { label: t.monitoring.nav.ric, to: '/admin/monitoring/ric' },
            { label: t.monitoring.nav.change_review, to: '/admin/monitoring/change-review' },
          ],
        },
      ],
    },
    {
      label: t.admin?.nav?.organizations || 'Organizations',
      icon: 'governance',
      requiredPermission: 'org:manage:read',
      moduleId: SIDEBAR_MODULE_MAP.organizations,
      children: [
        {
          label: t.admin.sidebar_groups.org_entities,
          children: [
            { label: t.admin?.nav?.org_list || 'All Organizations', to: '/admin/organizations' },
            {
              label:
                (t.admin.nav as unknown as Record<string, string>).departments || 'Departments',
              to: '/admin/organizations/departments',
            },
            { label: t.admin.nav.org_groups, to: '/admin/org-groups' },
          ],
        },
        {
          label: t.admin.sidebar_groups.org_config,
          children: [
            {
              label: t.admin?.nav?.intake_configs || 'Intake Configs',
              to: '/admin/intake-configs',
            },
            {
              label:
                ((t.admin as any)?.organizations?.retention_policies?.title as string) ||
                'Retention Policies',
              to: '/admin/organizations/retention-policies',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.org_frameworks,
          children: [
            {
              label:
                (t.admin as unknown as Record<string, Record<string, string>>).org_maturity
                  ?.framework_assignments || 'Framework Assignments',
              to: '/admin/organizations/entity-frameworks',
            },
            {
              label:
                (t.admin as unknown as Record<string, Record<string, string>>).org_maturity
                  ?.compliance_rollup || 'Compliance Rollup',
              to: '/admin/organizations/compliance-rollup',
            },
            {
              label:
                (t.admin.nav as unknown as Record<string, string>).consolidation || 'Consolidation',
              to: '/admin/consolidation',
            },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.partner_workspace,
      icon: 'governance',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.partner_workspace,
      children: [
        {
          label: t.admin.sidebar_groups.partner_overview,
          children: [
            {
              label: t.partner_marketplace.nav.dashboard,
              to: '/admin/partner-marketplace/dashboard',
            },
            { label: t.partner_marketplace.nav.reports, to: '/admin/partner-marketplace/reports' },
          ],
        },
        {
          label: t.admin.sidebar_groups.partner_partners,
          children: [
            {
              label: t.partner_marketplace.nav.partner_orgs,
              to: '/admin/partner-marketplace/partner-orgs',
            },
            {
              label: t.partner_marketplace.nav.brand_profiles,
              to: '/admin/partner-marketplace/brand-profiles',
            },
            {
              label: t.partner_marketplace.nav.service_tiers,
              to: '/admin/partner-marketplace/service-tiers',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.partner_delivery,
          children: [
            {
              label: t.partner_marketplace.nav.deliverables,
              to: '/admin/partner-marketplace/deliverables',
            },
            {
              label: t.partner_marketplace.nav.deliverable_templates,
              to: '/admin/partner-marketplace/deliverable-templates',
            },
            {
              label: t.partner_marketplace.nav.workflows,
              to: '/admin/partner-marketplace/workflows',
            },
            {
              label: t.partner_marketplace.nav.workflow_templates,
              to: '/admin/partner-marketplace/workflow-templates',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.partner_support,
          children: [
            {
              label: t.partner_marketplace.nav.support_tickets,
              to: '/admin/partner-marketplace/support-tickets',
            },
            {
              label: t.partner_marketplace.nav.escalation,
              to: '/admin/partner-marketplace/escalation',
            },
            { label: t.partner_marketplace.nav.audit, to: '/admin/partner-marketplace/audit' },
          ],
        },
      ],
    },
    {
      // Sprint 47.5.2 — Forseti AI Legal Advisor.
      // Sprint 47.9.9 — moved to immediately before Onboarding so the
      // platform-intelligence layer sits adjacent to the meta tooling
      // group (Onboarding / Platform / Help Center) rather than mid-
      // workflow between Discovery and Governance. Mirrors the manual
      // sidebar tree's bottom_standalone ordering at
      // `agent-os/scripts/help/sidebar-menu-tree.json`.
      label: t.admin.nav.forseti ?? 'Forseti',
      // The Forseti star, the launcher's own glyph (owner request 2026-11-03).
      icon: 'forseti',
      // Sprint 63.22.0 (OD-A6) — no group gate. The group was gated on
      // `AI_PROJECTS_READ` while its Chats leaf opens pages that require
      // `AI_ADVISOR_CHAT`, so a role holding only chat never saw the link to
      // the chats it could use. Each leaf now carries the permission its
      // route requires (`ROUTE_PERMISSIONS`), and the group shows when any
      // leaf does (`visibleMenuItems`).
      moduleId: SIDEBAR_MODULE_MAP.forseti,
      children: [
        // Sprint 47.5.4a — Chats sub-item (overview at /admin/forseti).
        {
          label: t.forseti?.chat?.sidebarLabel ?? 'Chats',
          to: '/admin/forseti',
          icon: 'chat',
          requiredPermission: Permission.AI_ADVISOR_CHAT,
        },
        {
          label: t.forseti?.projects?.title ?? 'Projects',
          to: '/admin/forseti/projects',
          icon: 'folder',
          requiredPermission: Permission.AI_PROJECTS_READ,
        },
        {
          label: t.forseti?.workflows?.pageTitle ?? 'Workflows',
          to: '/admin/forseti/workflows',
          icon: 'workflow',
          requiredPermission: Permission.AI_WORKFLOWS_READ,
        },
        {
          label: t.forseti?.tabularReviews?.pageTitle ?? 'Tabular Reviews',
          to: '/admin/forseti/tabular-reviews',
          icon: 'table',
          requiredPermission: Permission.AI_TABULAR_REVIEWS_READ,
        },
        {
          // Sprint 47.8.0a — surface the previously orphan
          // `/admin/forseti/document-review` route via a new index page.
          label: t.forseti?.documentReviewIndex?.sidebarLabel ?? 'Document Review',
          to: '/admin/forseti/document-review',
          icon: 'document_check',
          // The route's own gate (`ROUTE_PERMISSIONS` 'forseti/document-review')
          // and the index page's (`DocumentReviewIndexPage`). It was
          // `AI_ADVISOR_CHAT`, which showed a chat-only role a link to Access
          // Denied (gate E12.5 pass 1, P1).
          requiredPermission: Permission.AI_PROJECTS_READ,
        },
        {
          label: t.forseti?.auditLineage?.title ?? 'Audit Lineage',
          to: '/admin/forseti/audit-lineage',
          icon: 'lineage',
          requiredPermission: Permission.AI_ADVISOR_CHAT,
        },
        {
          // Sprint 47.7.3 — cross-framework conflict detection.
          label: t.forseti?.crossFrameworkConflicts?.pageTitle ?? 'Cross-framework conflicts',
          to: '/admin/forseti/cross-framework-conflicts',
          icon: 'overlap',
          requiredPermission: Permission.AI_ADVISOR_CHAT,
        },
        {
          // Sprint 47.5.8.6 — AI evidence review queue. R-34 hides if the caller
          // can't even chat with Forseti; the page itself is further gated by
          // the literal `evidence:review` permission inside the backend route.
          label: t.forseti?.evidenceDraft?.pageTitle ?? 'AI evidence drafts',
          to: '/admin/forseti/draft-evidence',
          icon: 'draft',
          requiredPermission: Permission.AI_ADVISOR_CHAT,
        },
        {
          label: t.forseti?.usage?.title ?? 'Usage',
          to: '/admin/forseti/usage',
          icon: 'chart',
          // The route's own gate (`ROUTE_PERMISSIONS` 'forseti/usage'). Without
          // it this ungated leaf would keep the group visible to every role.
          requiredPermission: Permission.AI_PROVIDER_VIEW_USAGE,
        },
        {
          // Owner ruling 2026-10-10 — the organisation's own AI provider,
          // model and effort. The route's own gate (`ROUTE_PERMISSIONS`
          // 'forseti/ai-model').
          label: t.forseti?.aiModel?.sidebarLabel ?? 'AI Model',
          to: '/admin/forseti/ai-model',
          icon: 'chip',
          requiredPermission: Permission.AI_PROVIDER_CONFIGURE,
        },
        {
          // Sprint 47.9.7 — MCP connector admin. Gated by its route alone
          // (`isLinkVisible` → `routeOpensFor`): since Sprint 63.23.0 gate
          // E12.5 pass 1 the route opens for `ai_provider:mcp:list` OR the
          // OAuth-client or grant list permission, one per register on the
          // page. A leaf gate here would hide the link from the latter two.
          label: t.forseti?.mcp?.pageTitle ?? 'MCP Keys',
          to: '/admin/forseti/mcp-keys',
          icon: 'key',
        },
        {
          // Sprint 48.3.0 — MCP Phase 2 pending writes admin queue.
          // Sprint 64.172.0 — gated on the queue's own read permission, not
          // the key inventory's. A reviewer who holds only the queue grants
          // would otherwise never see the nav leaf for the page they can use.
          label: (t as Record<string, any>).mcp_pending?.title ?? 'MCP Pending',
          to: '/admin/forseti/mcp-pending',
          icon: 'clock',
          requiredPermission: Permission.AI_PROVIDER_MCP_PENDING_LIST,
        },
        {
          // Sprint 48.0.0c — Forseti memory browse / curate. Visible to
          // anyone with `:read`; per R-34 hides for tenants without it.
          label: t.forseti?.memory?.pageTitle ?? 'Memory',
          to: '/admin/forseti/memory',
          icon: 'bookmark',
          requiredPermission: Permission.AI_ADVISOR_MEMORY_READ,
        },
        {
          // Sprint 48.2.8 — Compliance rule auto-mapping (regulatory pack
          // → control library).
          label:
            (t as Record<string, any>).compliance_rules?.rule_mapping?.title ?? 'Rule auto-mapping',
          to: '/admin/forseti/rule-mapping',
          icon: 'link',
          requiredPermission: Permission.AI_ADVISOR_CHAT,
        },
        {
          // Sprint 48.2.9 — Forseti audit walkthrough generator (forseti-auditor persona).
          label: (t as Record<string, any>).audit_walkthrough?.title ?? 'Audit walkthrough',
          to: '/admin/forseti/audit-walkthrough',
          icon: 'route',
          requiredPermission: Permission.AI_ADVISOR_CHAT,
        },
        {
          // Sprint 48.2.9 — Privilege creep detection (forseti-auditor persona).
          label: (t as Record<string, any>).privilege_creep?.title ?? 'Privilege creep',
          to: '/admin/forseti/privilege-creep',
          icon: 'trend_up',
          requiredPermission: Permission.AI_ADVISOR_CHAT,
        },
      ],
    },
    {
      label: t.admin.nav.onboarding,
      icon: 'onboarding',
      moduleId: SIDEBAR_MODULE_MAP.onboarding,
      // Each leaf uses its route grant; tour readers need not hold wizard access.
      children: [
        { label: t.onboarding.nav.wizard, to: '/admin/onboarding/setup', icon: 'flag' },
        { label: t.onboarding.nav.tours, to: '/admin/onboarding/tours', icon: 'compass' },
        { label: t.onboarding.nav.sample_data, to: '/admin/onboarding/sample-data', icon: 'data' },
        {
          label:
            (t.admin as unknown as Record<string, Record<string, string>>).onboarding_maturity
              ?.quick_starts || 'Quick Starts',
          to: '/admin/onboarding/quick-starts',
          icon: 'bolt',
        },
        {
          label:
            (t.admin as unknown as Record<string, Record<string, string>>).onboarding_maturity
              ?.progress || 'Onboarding Progress',
          to: '/admin/onboarding/progress',
          icon: 'progress',
        },
        {
          label:
            (t.admin as unknown as Record<string, Record<string, string>>).onboarding_maturity
              ?.personas || 'Persona Paths',
          to: '/admin/onboarding/personas',
          icon: 'people',
        },
      ],
    },
    {
      label: t.admin.nav.platform,
      icon: 'platform',
      requiredPermission: SIDEBAR_PERMISSION_MAP.platform,
      children: [
        {
          label: t.admin.sidebar_groups.platform_identity,
          icon: 'people',
          children: [
            { label: t.platform.nav.users, to: '/admin/users' },
            { label: t.platform.nav.roles, to: '/admin/roles' },
            { label: t.platform.security.role_analytics, to: '/admin/access-reviews/analytics' },
            { label: t.platform.security.service_accounts, to: '/admin/security/service-accounts' },
          ],
        },
        {
          label: t.admin.sidebar_groups.platform_access_control,
          icon: 'lock',
          children: [
            { label: t.platform.security.api_keys, to: '/admin/security/api-keys' },
            { label: t.platform.security.delegations, to: '/admin/security/delegations' },
            { label: t.platform.security.sod_rules, to: '/admin/sod-rules' },
            { label: t.platform.security.sod_violations, to: '/admin/sod-rules/violations' },
          ],
        },
        {
          label: t.admin.sidebar_groups.platform_auth_session,
          icon: 'login',
          children: [
            // Every user enrols their own device: the page asks for no permission.
            { label: t.platform.security.mfa, to: '/admin/security/mfa', selfService: true },
            ...(!isProduction
              ? [{ label: t.platform.security.sso, to: '/admin/security/sso' }]
              : []),
            { label: t.platform.security.active_sessions, to: '/admin/security/active-sessions' },
            { label: t.platform.security.session_settings, to: '/admin/security/session-settings' },
            { label: t.platform.security.ip_allowlist, to: '/admin/security/ip-allowlist' },
          ],
        },
        {
          label: t.admin.sidebar_groups.platform_access_reviews,
          icon: 'clipboard_check',
          children: [
            { label: t.platform.security.access_reviews, to: '/admin/access-reviews' },
            {
              label: t.platform.security.my_reviews,
              to: '/admin/access-reviews/my-reviews',
              selfService: true,
            },
            {
              label: t.platform.security.attestation_schedules,
              to: '/admin/attestation-schedules',
            },
            {
              label: (t as any).assessment_triggers?.title ?? 'Assessment Triggers',
              to: '/admin/settings/assessment-triggers',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.platform_files,
          icon: 'file',
          children: [
            // Sprint 41.4.0 — Platform → Files (Configuration / Browser / Dashboard)
            {
              label:
                (t.platform as unknown as Record<string, Record<string, string>>)?.files_nav
                  ?.configuration ?? 'Files Configuration',
              to: '/admin/platform/files/config',
              requiredPermission: 'platform:files:config:read',
            },
            {
              label:
                (t.platform as unknown as Record<string, Record<string, string>>)?.files_nav
                  ?.browser ?? 'File Browser',
              to: '/admin/platform/files/browser',
              requiredPermission: 'platform:files:browse',
            },
            {
              label:
                (t.platform as unknown as Record<string, Record<string, string>>)?.files_nav
                  ?.dashboard ?? 'Files Dashboard',
              to: '/admin/platform/files/dashboard',
              requiredPermission: 'platform:files:metrics:read',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.platform_notifications,
          icon: 'bell',
          children: [
            {
              label:
                (t.notifications as unknown as Record<string, Record<string, string>>)?.admin_queue
                  ?.title ?? 'Notification Admin Queue',
              to: '/admin/platform/notification-admin-queue',
              requiredPermission: 'platform:notifications:admin',
            },
            {
              label: t.notifications?.preferences?.title ?? 'Notification Preferences',
              to: '/admin/platform/notification-preferences',
              requiredPermission: Permission.PLATFORM_NOTIFICATIONS_PREFERENCES,
              selfService: true,
            },
            // Sprint 58.5.0 post-merge QA fix — PDPL Evidence Pack email-providers
            // moved from a top-level section (E1b placement) into Platform → Notifications.
            // The pack flow itself is deep-linked from DPIA dossiers; only the
            // provider configuration belongs in the platform-wide config namespace.
            {
              label: t.pdplEvidencePacks.nav.emailProviders,
              to: '/admin/pdpl-evidence-packs/email-providers',
            },
          ],
        },
        {
          // Sprint 47.4.0 — Platform → Audit & Proof (foundation shells).
          // Editable surfaces ship in 47.4.4 (usage), 47.4.5 (status),
          // 47.4.6 (integrity center), 47.4.7 (settings).
          label:
            (t.platform as unknown as Record<string, Record<string, string>>)
              ?.immutable_audit_trails?.nav_group_label ?? 'Audit & Proof',
          icon: 'audit',
          children: [
            {
              label:
                (t.platform as unknown as Record<string, Record<string, string>>)
                  ?.immutable_audit_trails?.nav_settings ?? 'Settings',
              to: '/admin/platform/immutable-audit-trails/settings',
              requiredPermission: 'platform:immutable_audit_trails:read',
            },
            {
              label:
                (t.platform as unknown as Record<string, Record<string, string>>)
                  ?.immutable_audit_trails?.nav_status ?? 'Proof Status',
              to: '/admin/platform/immutable-audit-trails/status',
              requiredPermission: 'platform:immutable_audit_trails:read',
            },
            {
              label:
                (t.platform as unknown as Record<string, Record<string, string>>)
                  ?.immutable_audit_trails?.nav_integrity_center ?? 'Integrity Center',
              to: '/admin/platform/immutable-audit-trails/integrity-center',
              requiredPermission: 'platform:immutable_audit_trails:read',
            },
            {
              label:
                (t.platform as unknown as Record<string, Record<string, string>>)
                  ?.immutable_audit_trails?.nav_usage ?? 'Usage & Credits',
              to: '/admin/platform/immutable-audit-trails/usage',
              requiredPermission: 'platform:immutable_audit_trails:read',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.platform_module_config,
          icon: 'settings',
          children: [
            { label: t.platform.module_config.nav_label, to: '/admin/platform/modules' },
            { label: t.platform.module_config.settings, to: '/admin/platform/settings' },
            // Sprint 63.37.0 (E0) — the organisation's identity, read by official forms.
            {
              label: t.org_identity.nav_label,
              to: '/admin/platform/organisation-identity',
              requiredPermission: 'platform:settings:read',
            },
            // Sprint 62.53.0 (AC9) — the Organization profile page (sectors and the
            // recorded profile statements). Its reads and writes are the platform
            // security permissions, as the route table says (`settings/sectors`).
            {
              label: t.org_profile_statements.nav_label,
              to: '/admin/settings/sectors',
              requiredPermission: 'platform:security:read',
            },
            { label: t.platform.module_config.domains, to: '/admin/platform/domains' },
            { label: t.platform.module_config.webhooks, to: '/admin/platform/webhooks' },
          ],
        },
        {
          label: t.admin.sidebar_groups.platform_ops,
          icon: 'log',
          children: [
            { label: t.platform.module_config.email_log, to: '/admin/platform/email' },
            {
              label: t.platform.module_config.export_schedules,
              to: '/admin/platform/export-schedules',
            },
          ],
        },
        // Standalone leaves (kept top-level within the Platform module):
        // Break Glass is a deliberate red-button surface; Profile + License
        // are user-level concerns that benefit from quick access.
        {
          label: t.platform.security.break_glass,
          to: '/admin/security/break-glass',
          icon: 'unlock',
        },
        { label: t.platform.nav.profile, to: '/admin/profile', icon: 'user_circle' },
        {
          label: t.license.title,
          to: '/admin/settings/license',
          icon: 'badge',
          requiredPermission: 'license:read',
        },
      ],
    },
    {
      label: t.admin.nav.privacy_scanner,
      icon: 'discovery',
      requiredPermission: SIDEBAR_PERMISSION_MAP.privacy_scanner,
      moduleId: SIDEBAR_MODULE_MAP.privacy_scanner,
      children: [
        { label: t.admin.nav.privacy_monitoring, to: '/admin/privacy-scanner/monitoring' },
        { label: t.admin.nav.real_time_scanner, to: '/admin/privacy-scanner/real-time' },
      ],
    },
    {
      label: t.admin.nav.program_governance,
      icon: 'assessments',
      // No section gate. Each page here is served on the grant its own API routes ask for
      // (see `route-permissions.ts`), and `maturity_model:read` is only the catch-all of the
      // pages with none of their own. A section gate on top hid the whole section from a role
      // holding just one page's grant. The section shows when any of its links does
      // (`visibleMenuItems`), and each link is shown only when its route opens (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.program_governance,
      children: [
        {
          label: t.admin.sidebar_groups.pg_health,
          children: [
            {
              label: t.program_governance.nav.program_health,
              to: '/admin/program-governance/health',
            },
            {
              label: t.program_governance.nav.trend_dashboard,
              to: '/admin/program-governance/trends',
            },
            {
              label: t.program_governance.nav.executive_summaries,
              to: '/admin/program-governance/summaries',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.pg_maturity,
          children: [
            {
              label: t.program_governance.nav.maturity_assessments,
              to: '/admin/program-governance/assessments',
            },
            {
              label: t.program_governance.nav.handbook_assessments,
              to: '/admin/program-governance/handbook-assessments',
            },
            {
              label: t.program_governance.nav.maturity_models,
              to: '/admin/program-governance/models',
            },
            {
              label: t.program_governance.nav.governance_model,
              to: '/admin/program-governance/governance-model',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.pg_audit,
          children: [
            {
              /*
               * Sprint 65.8.0 (AC9) — the audit engagement register.
               *
               * A leaf of the existing audit sub-group, not a new module and
               * not a new group: it is the RECORD the readiness score beside it
               * is a score about, and the evidence-gap and remediation
               * registers below it are its neighbours in purpose. It leads the
               * group because an engagement is what the other three are read
               * in support of.
               */
              label: t.program_governance.nav.audit_engagements,
              to: '/admin/program-governance/audit-engagements',
            },
            {
              label: t.program_governance.nav.audit_readiness,
              to: '/admin/program-governance/readiness',
            },
            {
              label: t.program_governance.nav.evidence_gaps,
              to: '/admin/program-governance/evidence-gaps',
            },
            {
              label: t.program_governance.nav.remediation_plans,
              to: '/admin/program-governance/remediation-plans',
            },
            {
              label: t.program_governance.nav.reporting_periods,
              to: '/admin/program-governance/reporting-periods',
            },
            // Fix sprint 20260927-0100 F7 (owner ruling 2026-09-27) — the four
            // Phase 28 DPO registers that sat here (de-identification runs,
            // continuity exercises, legal matters, credit-information uses)
            // and the Training and Privacy Office sub-groups below moved to
            // Assessments › Privacy Office / Training: they serve every tier,
            // and Program Governance is Standard and above.
          ],
        },
        {
          label: t.admin.sidebar_groups.pg_obligations,
          children: [
            {
              label: t.program_governance.nav.obligation_mappings,
              to: '/admin/program-governance/obligation-mappings',
            },
            {
              label: t.program_governance.nav.change_log,
              to: '/admin/program-governance/change-log',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.pg_documents,
          children: [
            {
              label: t.program_governance.nav.governance_documents,
              to: '/admin/program-governance/governance-documents',
            },
            {
              label: t.program_governance.nav.document_drafting,
              to: '/admin/program-governance/document-drafting',
            },
            {
              label: t.program_governance.nav.document_issuance,
              to: '/admin/program-governance/document-issuance',
            },
            {
              label: t.program_governance.nav.acknowledgment_tracking,
              to: '/admin/program-governance/acknowledgment-tracking',
            },
            {
              label: t.program_governance.nav.review_cadence,
              to: '/admin/program-governance/review-cadence',
            },
          ],
        },
        // ── Sprint 62.8.0 — manufacturing operations ────────────
        //
        // Sprint 63.43.0 (OR-26): the overlay-exclusive leaves (batches, trace,
        // EPR, chemicals, chemical traceability) moved to Sector Overlays ›
        // Manufacturing; products, conformity and CAPA & recall stay here.
        //
        // Seven leaves in two sub-groups, under an **existing** module. A new
        // top-level module would need a profile in
        // `docs/content/modules-overview.md`, which is the commercial tiering
        // document, and would declare a 30th sellable module — moving Light 15 /
        // Standard 26 / Enterprise 29, a product decision arriving as a side
        // effect of a sidebar edit (G-110). Those counts are unchanged.
        //
        // Program Governance owns them because its profile is obligation
        // mapping, register-keeping and gap-to-remediation, and product
        // conformity, producer responsibility and substance control are
        // obligation registers with evidence and open findings. The OT leaves
        // are deliberately elsewhere — see the Data Mapping block — because the
        // canonical system store they extend lives there.
        //
        // Labels come from `extractLocale`, not from a locale bundle: two new
        // keys would break the 15-locale deep-parity test, and EN + VI is this
        // series' scope. Zero new locale keys.
        {
          label: extractLocale(
            {
              en: 'Product Compliance & Traceability',
              vi: 'Tuân thủ sản phẩm và truy xuất nguồn gốc',
            },
            locale,
          ),
          children: [
            {
              label: extractLocale({ en: 'Product Register', vi: 'Sổ đăng ký sản phẩm' }, locale),
              to: '/admin/manufacturing/products',
              requiredPermission: Permission.MANUFACTURING_PRODUCT_READ,
            },
            {
              label: extractLocale({ en: 'Conformity Evidence', vi: 'Bằng chứng hợp quy' }, locale),
              to: '/admin/manufacturing/conformity',
              requiredPermission: Permission.MANUFACTURING_CONFORMITY_READ,
            },
            {
              label: extractLocale(
                { en: 'CAPA & Recall', vi: 'Hành động khắc phục và thu hồi' },
                locale,
              ),
              to: '/admin/manufacturing/capa-recall',
              requiredPermission: Permission.MANUFACTURING_RECALL_READ,
            },
          ],
        },
        // ── Industrial OT Assurance, under Program Governance ────
        //
        // **The same sub-group name as the one under Data Mapping, deliberately.**
        //
        // Remote Access Assurance first landed inside *Producer & Substance
        // Obligations*, and Stream G raised the objection that closed it: a grant
        // of vendor remote access into machinery is neither a producer obligation
        // nor a substance obligation, so that group's name had become narrower
        // than its contents. That is 62.6.0's shape — an internally consistent
        // naming set that does not describe what it holds — and the reviewer
        // pointed out, correctly, that adding an honest sentence to the brief
        // would be describing a defect accurately, which is exactly what this
        // sprint declined to do about the tier placement itself.
        //
        // So the group is named for its subject, and it carries the **same name**
        // as its sibling under Data Mapping. Two identically-named sub-groups in
        // two modules is not a collision — a sub-group is scoped to its module —
        // and it is the honest description: this is *one* capability area whose two
        // leaves depend on different things and therefore sit in different tiers.
        // An asset profile extends a canonical system (Light); a grant names a
        // vendor (Standard). Naming them the same tells a user hunting for
        // industrial OT that both places are the right place, where a contrived
        // fourth name would imply two unrelated capabilities.
        {
          label: extractLocale(
            { en: 'Industrial OT Assurance', vi: 'Bảo đảm an ninh OT công nghiệp' },
            locale,
          ),
          children: [
            // ── Remote Access Assurance, moved here mid-sprint ────
            //
            // Gate B0 froze this leaf under **Data Mapping**, beside OT Asset
            // Profiles, on the ground that OT extends the canonical system store
            // which lives there. That reasoning holds for the asset register and
            // **fails for this one**, and the failure was found by tracing the
            // service's own reference check rather than by reading the layout:
            //
            // `RemoteAccessGrant.vendor_id` must resolve in `grc_vendors`, and
            // every surface that creates a vendor lives in `governance` /
            // `vendor-risk` — **both Standard**. Data Mapping is **Light**. So on
            // the essential package this page would have rendered, listed empty,
            // and been unable to record a single grant, for ever: a control that
            // cannot be used, on a page that looks finished. 62.0.11 found 120 of
            // those and settled that an honest absence beats a dead control.
            //
            // It sits under Program Governance rather than Data Mapping because the
            // dependency that decides the tier is the **vendor**, not the system: a
            // remote-access grant is a statement about a third party the
            // organisation let into its machinery. Splitting the two OT leaves on
            // their actual dependencies is more honest than keeping them adjacent.
            //
            // The two OT leaves are therefore in different modules on purpose, and
            // the help content is split the same way — a Light tenant reads about
            // the asset register it has and is not shown a page it cannot use.
            {
              label: extractLocale(
                { en: 'Remote Access Assurance', vi: 'Bảo đảm truy cập từ xa' },
                locale,
              ),
              to: '/admin/manufacturing/remote-access',
              // `:read`, not `:approve_remote_access`. Seeing who holds access —
              // and which grants have expired — is the register anyone with read
              // access needs; approving is the gated act, at the control and at
              // the route.
              requiredPermission: Permission.MANUFACTURING_OT_READ,
            },
          ],
        },
        // ── Sprint 62.9.0 — logistics, customs, parcel and delivery ──
        //
        // Sprint 63.43.0 (OR-26): the overlay-exclusive leaves (customs evidence,
        // duty-free lists, tax examinations, tax transition, claims, postal
        // authorisations) moved to Sector Overlays › Transport & Logistics;
        // shipments, inspections, tax dossiers, tax payments, parcels and
        // delivery stay here.
        //
        // Seven leaves in two sub-groups, under the **same existing module**. A
        // new top-level module would need a profile in
        // `docs/content/modules-overview.md` — the commercial tiering document —
        // and would declare a 30th sellable module, moving Light 15 / Standard 26
        // / Enterprise 29 as a side effect of a sidebar edit (G-110). Those counts
        // are unchanged; sidebar leaves go 319 → 326.
        //
        // **The tier is derived from the dependencies, not chosen** — 62.8.0's
        // broadcast #10, where a page placed on Light could never have recorded a
        // single grant because the vendor it must resolve lives in a Standard
        // module. A shipment's carrier and customs broker resolve in `grc_vendors`,
        // whose every creating surface is in Governance / Vendor Risk (Standard),
        // and the register-keeping-with-evidence-and-findings profile is Program
        // Governance's own.
        //
        // Nothing goes in Data Mapping this time. The cross-border and
        // personal-data half composes `DataFlow` / `ProcessingActivity` /
        // `DataSubjectProfile` **through references**, so it adds no leaf — which
        // is the correct outcome of a reuse check rather than a gap.
        //
        // Labels come from `extractLocale`, not from a locale bundle: new keys
        // would break the 15-locale deep-parity test, and EN + VI is this series'
        // scope. Zero new locale keys.
        //
        // The names below are Gate B0's frozen contract, **including the two
        // corrections broadcast mid-sprint** and pinned against the help article
        // and `modules-overview.vi.md` by `__tests__/strings-terminology.test.ts`
        // — never against `strings.ts`, which is the guard 62.5.0 was missing.
        {
          label: extractLocale(
            { en: 'Trade & Customs Evidence', vi: 'Bằng chứng thương mại và hải quan' },
            locale,
          ),
          children: [
            {
              label: extractLocale({ en: 'Shipment Register', vi: 'Sổ đăng ký lô hàng' }, locale),
              to: '/admin/logistics/shipments',
              requiredPermission: Permission.LOGISTICS_SHIPMENT_READ,
            },
            {
              label: extractLocale(
                { en: 'Inspections & Holds', vi: 'Kiểm tra và tạm giữ' },
                locale,
              ),
              to: '/admin/logistics/inspections',
              // `logistics_customs:read`, not `:clearance:append`. What the
              // authority did about a consignment is customs evidence anyone with
              // the read grant needs; appending to the ledger is the gated act, at
              // the control and at the route.
              requiredPermission: Permission.LOGISTICS_CUSTOMS_READ,
            },
            // Sprint 62.55.0 — the customs tax-administration registers shared with
            // baseline frameworks: the organization's own dossiers before the
            // customs authority and the documents received back, and payments and
            // their clocks (the other three are under Sector Overlays). Labels are the B0 names as ruled (R-44,
            // 2026-10-24) and equal each page's title; every leaf is on the
            // module's read grant (`isLinkVisible` also checks the route table).
            {
              label: extractLocale(
                {
                  en: 'Tax Dossiers and Received Documents',
                  vi: 'Hồ sơ thuế và văn bản nhận được từ cơ quan hải quan',
                },
                locale,
              ),
              to: '/admin/logistics/tax-dossiers',
              requiredPermission: Permission.CUSTOMS_TAX_ADMINISTRATION_READ,
            },
            {
              label: extractLocale(
                { en: 'Tax Payments and Due Dates', vi: 'Nộp thuế và thời hạn nộp thuế' },
                locale,
              ),
              to: '/admin/logistics/tax-payments',
              requiredPermission: Permission.CUSTOMS_TAX_ADMINISTRATION_READ,
            },
          ],
        },
        {
          label: extractLocale(
            { en: 'Parcel & Delivery Evidence', vi: 'Bằng chứng bưu gửi và giao hàng' },
            locale,
          ),
          children: [
            {
              label: extractLocale({ en: 'Parcel Register', vi: 'Sổ đăng ký bưu gửi' }, locale),
              to: '/admin/logistics/parcels',
              requiredPermission: Permission.LOGISTICS_PARCEL_READ,
            },
            {
              // `Theo dõi và bằng chứng giao hàng`, not the shorter
              // `Bằng chứng giao hàng`: in the pack corpus that phrase means proof
              // of delivery specifically, while this leaf is the whole append-only
              // timeline — so a Vietnamese operator looking for a parcel's scan
              // history would not have looked there. `theo dõi` is the pack's own
              // word for tracking, 14 occurrences, so the correction is quoted
              // rather than invented.
              label: extractLocale(
                { en: 'Delivery Evidence', vi: 'Theo dõi và bằng chứng giao hàng' },
                locale,
              ),
              to: '/admin/logistics/delivery',
              // The parcel grant, not the proof grant. The timeline is the parcel
              // register's; the proof panel on that page carries
              // `logistics_delivery_proof:read` for itself and renders nothing at
              // all without it.
              requiredPermission: Permission.LOGISTICS_PARCEL_READ,
            },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.rights_requests,
      icon: 'rights',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.rights_requests,
      children: [
        { label: t.admin.nav.rights_requests, to: '/admin/rights-requests' },
        {
          label: t.rights_requests?.import?.nav_title ?? 'Bulk Import',
          to: '/admin/rights-requests/import',
        },
        // Sprint 48.1.1 — DSR Batch Process (Forseti AI workflow).
        {
          label: t.rights_requests?.batch?.nav_title ?? 'Batch Process',
          to: '/admin/rights-requests/batch-autocomplete',
          requiredPermission: Permission.AI_ADVISOR_CHAT,
        },
        /* Sprint 43.5.1 consolidation — the Phase 1 Sprint 1.1.0 TemplatesPage
         * at `/admin/templates` and the Sprint 43.2.0 ResponseTemplatesAdminPage
         * at `/admin/rights-requests/response-templates` both appeared side-by-side
         * under Rights Requests in the sidebar, both rendering as "Response
         * Templates". The newer DsrResponseTemplate CRUD (outcome types,
         * official_form_id, is_active soft-delete) is canonical — keep only that
         * entry. The old `/admin/templates` route is left intact for backwards
         * compatibility with any bookmarked URLs, but no longer navigable
         * from the sidebar.
         */
        {
          label:
            ((t.admin as any)?.rights_requests?.response_templates?.title as string) ||
            'Response Templates',
          to: '/admin/rights-requests/response-templates',
        },
        ...(!isProduction
          ? [
              {
                label: t.admin.nav.cmp_dsar_sync,
                to: '/admin/rights-requests/settings/cmp-integration',
              },
            ]
          : []),
      ],
    },
    {
      label: t.module_labels.sector_overlays,
      icon: 'classification',
      // Sprint 63.43.0 (owner ruling OR-26) — Module 30, Sector Overlays,
      // Enterprise only: the section is hidden wherever the `sector_overlays`
      // module is disabled (Light and Standard), and wherever the navigation
      // projection answers `PACKAGE_LIMIT` (`sectorOverlaysNotEntitled`, fix
      // sprint 20261102-0448 F5). No section gate: each leaf is
      // shown on its own route's grant (`isLinkVisible`). Overlay Management is
      // always drawn; a sector leaf is drawn only while an operational overlay
      // declares into its registry view (`hiddenOverlayRoutes`), and a group
      // with no visible leaf is not drawn. Leaves keep their labels, routes
      // and permissions from their old homes (plan §3.1); the first leaf is
      // the Overview, so the derived path is `/admin/sector-overlays`.
      moduleId: SIDEBAR_MODULE_MAP.sector_overlays,
      children: [
        {
          label: t.admin.sidebar_groups.sector_overlays_management,
          icon: 'sliders',
          children: [
            {
              label: t.sector_overlays_module.nav.overview,
              to: '/admin/sector-overlays',
              // Shown on its route's grant (`isLinkVisible`): `market_pack:read`.
            },
            {
              label: t.localization.nav.sector_overlay_catalog,
              to: '/admin/localization/sector-overlays',
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.sector_overlays_banking_payments,
          icon: 'card',
          children: [
            // Sprint 62.24.0 — the register the September banking instrument left
            // without a surface.
            {
              label: extractLocale(
                { en: 'Credit-Information Supply Register', vi: 'Sổ cung cấp thông tin tín dụng' },
                locale,
              ),
              to: '/admin/credit-information-supply',
              requiredPermission: Permission.CREDIT_INFORMATION_SUPPLY_READ,
            },
            // Sprint 62.50.0 — the supervision-information registers: shared
            // information received, the regional branch's requirements (publicity
            // and information requests), depositor enquiries and the organization's
            // own Supervision Information System records. Each on the overlay's read grant.
            {
              label: extractLocale(
                { en: 'Shared Supervisory Information', vi: 'Thông tin, báo cáo được chia sẻ' },
                locale,
              ),
              to: '/admin/pcf-supervision/shared-information',
              requiredPermission: Permission.PCF_SUPERVISION_READ,
            },
            {
              label: extractLocale(
                {
                  en: 'Branch Publicity Evidence',
                  vi: 'Thông báo, công khai theo yêu cầu của chi nhánh',
                },
                locale,
              ),
              to: '/admin/pcf-supervision/publicity',
              requiredPermission: Permission.PCF_SUPERVISION_READ,
            },
            {
              label: extractLocale(
                {
                  en: 'Branch Information Requests',
                  vi: 'Yêu cầu cung cấp thông tin của chi nhánh',
                },
                locale,
              ),
              to: '/admin/pcf-supervision/information-requests',
              requiredPermission: Permission.PCF_SUPERVISION_READ,
            },
            {
              label: extractLocale(
                { en: 'Depositor Enquiries', vi: 'Câu hỏi của khách hàng gửi tiền' },
                locale,
              ),
              to: '/admin/pcf-supervision/depositor-enquiries',
              requiredPermission: Permission.PCF_SUPERVISION_READ,
            },
            {
              label: extractLocale(
                { en: 'Supervision System Records', vi: 'Hồ sơ Hệ thống thông tin giám sát' },
                locale,
              ),
              to: '/admin/pcf-supervision/system-records',
              requiredPermission: Permission.PCF_SUPERVISION_READ,
            },
            {
              // Sprint 62.38.0 — the reporting role, indicator lineage, the
              // pre-submission check, flagged-data corrections and on-site
              // inspection evidence. The platform supervises nothing and
              // submits nothing; it records what the organisation produced.
              label: extractLocale({ en: 'Supervisory Reporting', vi: 'Báo cáo giám sát' }, locale),
              to: '/admin/payments-evidence/supervisory-reporting',
              requiredPermission: Permission.PAYMENTS_EVIDENCE_READ,
            },
            {
              label: extractLocale({ en: 'Payment Services', vi: 'Dịch vụ thanh toán' }, locale),
              to: '/admin/payments-evidence/services',
              requiredPermission: Permission.PAYMENTS_EVIDENCE_READ,
            },
            {
              label: extractLocale({ en: 'eKYC Evidence', vi: 'Bằng chứng eKYC' }, locale),
              to: '/admin/payments-evidence/verifications',
              requiredPermission: Permission.PAYMENTS_EVIDENCE_READ,
            },
            {
              label: extractLocale({ en: 'Exception Review', vi: 'Rà soát ngoại lệ' }, locale),
              to: '/admin/payments-evidence/exceptions',
              requiredPermission: Permission.PAYMENTS_EVIDENCE_READ,
            },
            {
              // `Đối chiếu` — comparing two things — because this record
              // compares two *duties* that point in opposite directions. The
              // platform-evidence leaf below says `đối soát`, reconciling
              // records against each other, which is the pack's own word and a
              // different act.
              label: extractLocale(
                { en: 'Retention Reconciliation', vi: 'Đối chiếu nghĩa vụ lưu trữ' },
                locale,
              ),
              to: '/admin/payments-evidence/retention',
              requiredPermission: Permission.PAYMENTS_EVIDENCE_READ,
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.sector_overlays_crypto_assets,
          icon: 'coin',
          children: [
            {
              // Sprint 62.46.0 — the reporting-entity scoping, records-retention
              // scope, indicator coverage, beneficial owners, pre-launch
              // assessment and guidance tracking. The register records what the
              // organisation decided; it determines nothing and sends nothing.
              label: extractLocale(
                { en: 'AML Programme', vi: 'Chương trình phòng, chống rửa tiền' },
                locale,
              ),
              to: '/admin/payments-evidence/aml-programme',
              requiredPermission: Permission.PAYMENTS_EVIDENCE_READ,
            },
            {
              // Sprint 62.46.0 — restricted: only `aml_case:read` sees this leaf,
              // so the navigation itself does not tell anyone else a case
              // register is in use.
              label: extractLocale(
                { en: 'Suspicious-Activity Cases', vi: 'Vụ việc giao dịch đáng ngờ' },
                locale,
              ),
              to: '/admin/payments-evidence/aml-cases',
              requiredPermission: Permission.AML_CASE_READ,
            },
            {
              label: extractLocale(
                { en: 'Investor Verifications', vi: 'Xác minh nhà đầu tư' },
                locale,
              ),
              to: '/admin/investor-verifications',
              requiredPermission: Permission.INVESTOR_VERIFICATION_READ,
            },
            {
              label: extractLocale(
                { en: 'Account Data Disclosures', vi: 'Cung cấp dữ liệu tài khoản' },
                locale,
              ),
              to: '/admin/account-data-disclosures',
              requiredPermission: Permission.ACCOUNT_DATA_DISCLOSURE_READ,
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.sector_overlays_manufacturing,
          icon: 'factory',
          children: [
            {
              label: extractLocale(
                { en: 'Batch & Serial Register', vi: 'Sổ đăng ký lô và số sê-ri' },
                locale,
              ),
              to: '/admin/manufacturing/batches',
              requiredPermission: Permission.MANUFACTURING_PRODUCT_READ,
            },
            {
              label: extractLocale(
                { en: 'Trace Timeline', vi: 'Dòng thời gian truy xuất' },
                locale,
              ),
              to: '/admin/manufacturing/trace',
              // `:read`, not `:append`. Reading the chain is what a recall, an
              // audit and a customer complaint all need; `:append` gates adding
              // to it, at the control and at the route.
              requiredPermission: Permission.MANUFACTURING_PRODUCT_READ,
            },
            {
              label: extractLocale({ en: 'EPR Reconciliation', vi: 'Đối chiếu EPR' }, locale),
              to: '/admin/manufacturing/epr',
              requiredPermission: Permission.MANUFACTURING_EPR_READ,
            },
            {
              label: extractLocale({ en: 'Chemical Register', vi: 'Sổ đăng ký hóa chất' }, locale),
              to: '/admin/manufacturing/chemicals',
              requiredPermission: Permission.MANUFACTURING_SUBSTANCE_READ,
            },
            // Sprint 63.31.0 (#444, #445) — declarations and code terminations
            // an installed chemical-traceability overlay declares clocks for.
            {
              label: extractLocale(
                {
                  en: 'Chemical Traceability Declarations',
                  vi: 'Kê khai truy xuất nguồn gốc hóa chất',
                },
                locale,
              ),
              to: '/admin/manufacturing/chemical-traceability',
              requiredPermission: Permission.MANUFACTURING_SUBSTANCE_READ,
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.sector_overlays_food_traceability,
          icon: 'leaf',
          children: [
            {
              label: extractLocale(
                { en: 'Food Establishment', vi: 'Hồ sơ cơ sở thực phẩm' },
                locale,
              ),
              to: '/admin/food-traceability/establishment',
              requiredPermission: Permission.FOOD_TRACEABILITY_READ,
            },
            {
              label: extractLocale(
                { en: 'Lot Test Results', vi: 'Kết quả kiểm nghiệm theo lô' },
                locale,
              ),
              to: '/admin/food-traceability/lot-results',
              requiredPermission: Permission.FOOD_TRACEABILITY_READ,
            },
            {
              label: extractLocale(
                { en: 'Food Inspections', vi: 'Kiểm tra an toàn thực phẩm' },
                locale,
              ),
              to: '/admin/food-traceability/inspections',
              requiredPermission: Permission.FOOD_TRACEABILITY_READ,
            },
            {
              label: extractLocale({ en: 'Warnings', vi: 'Cảnh báo thực phẩm' }, locale),
              to: '/admin/food-traceability/warnings',
              requiredPermission: Permission.FOOD_TRACEABILITY_READ,
            },
            {
              label: extractLocale(
                { en: 'Notices and Quantities', vi: 'Thông báo và số lượng' },
                locale,
              ),
              to: '/admin/food-traceability/notices',
              requiredPermission: Permission.FOOD_TRACEABILITY_READ,
            },
            {
              label: extractLocale(
                { en: 'Violation Decisions', vi: 'Quyết định xử lý vi phạm' },
                locale,
              ),
              to: '/admin/food-traceability/decisions',
              requiredPermission: Permission.FOOD_TRACEABILITY_READ,
            },
            {
              label: extractLocale(
                {
                  en: 'Corrections and Supplier Corrections',
                  vi: 'Hiệu chỉnh dữ liệu và hiệu chỉnh của nhà cung cấp',
                },
                locale,
              ),
              to: '/admin/food-traceability/corrections',
              requiredPermission: Permission.FOOD_TRACEABILITY_READ,
            },
            {
              label: extractLocale({ en: 'Complaints', vi: 'Khiếu nại về thực phẩm' }, locale),
              to: '/admin/food-traceability/complaints',
              requiredPermission: Permission.FOOD_TRACEABILITY_READ,
            },
            {
              label: extractLocale(
                { en: 'Connection Routes', vi: 'Tuyến kết nối dữ liệu' },
                locale,
              ),
              to: '/admin/food-traceability/routes',
              requiredPermission: Permission.FOOD_TRACEABILITY_READ,
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.sector_overlays_transport_logistics,
          icon: 'truck',
          children: [
            {
              label: extractLocale(
                { en: 'Regulated Accounts', vi: 'Tài khoản chịu quản lý' },
                locale,
              ),
              to: '/admin/regulated-accounts',
              requiredPermission: Permission.REGULATED_ACCOUNT_READ,
            },
            {
              label: extractLocale({ en: 'Customs Evidence', vi: 'Bằng chứng hải quan' }, locale),
              to: '/admin/logistics/customs',
              requiredPermission: Permission.LOGISTICS_CUSTOMS_READ,
            },
            {
              label: extractLocale(
                {
                  en: 'Duty-Free Lists and Usage Notifications',
                  vi: 'Danh mục miễn thuế và thông báo tình hình sử dụng hàng hóa miễn thuế',
                },
                locale,
              ),
              to: '/admin/logistics/duty-free-lists',
              requiredPermission: Permission.CUSTOMS_TAX_ADMINISTRATION_READ,
            },
            {
              label: extractLocale({ en: 'Tax Examinations', vi: 'Kiểm tra thuế' }, locale),
              to: '/admin/logistics/tax-examinations',
              requiredPermission: Permission.CUSTOMS_TAX_ADMINISTRATION_READ,
            },
            {
              label: extractLocale(
                { en: 'Transition and Applicability', vi: 'Chuyển tiếp và quy định áp dụng' },
                locale,
              ),
              to: '/admin/logistics/tax-transition',
              requiredPermission: Permission.CUSTOMS_TAX_ADMINISTRATION_READ,
            },
            {
              label: extractLocale({ en: 'Claims', vi: 'Khiếu nại' }, locale),
              to: '/admin/logistics/claims',
              requiredPermission: Permission.LOGISTICS_CLAIM_READ,
            },
            {
              // `Giấy phép và thông báo hoạt động bưu chính`, not the shorter
              // form: the pack says `thông báo hoạt động bưu chính` — *activity*
              // notification — eight times against one standalone short use, and
              // the regime keeps a licence and a written confirmation of an
              // activity notification apart as two instruments with two processes.
              // That separation is the entire reason `authorisation_kind` is this
              // sprint's one jurisdiction-specific vocabulary, and a name blurring
              // it would describe the register as something other than what it
              // records.
              label: extractLocale(
                {
                  en: 'Postal Authorisations',
                  vi: 'Giấy phép và thông báo hoạt động bưu chính',
                },
                locale,
              ),
              to: '/admin/logistics/authorisations',
              requiredPermission: Permission.LOGISTICS_AUTHORISATION_READ,
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.sector_overlays_public_administration,
          icon: 'landmark',
          children: [
            {
              label: extractLocale({ en: 'Dataset Catalog', vi: 'Danh mục tập dữ liệu' }, locale),
              to: '/admin/public-data/datasets',
              requiredPermission: Permission.PUBLIC_DATA_READ,
            },
            {
              label: extractLocale({ en: 'Sharing Register', vi: 'Sổ đăng ký chia sẻ' }, locale),
              to: '/admin/public-data/sharing',
              requiredPermission: Permission.PUBLIC_DATA_READ,
            },
            {
              label: extractLocale({ en: 'Open Data Releases', vi: 'Công bố dữ liệu mở' }, locale),
              to: '/admin/public-data/releases',
              // `:read`, not `:release`. Seeing what has been published — and
              // what is blocked from publication — is a register anyone with
              // read access needs; `:release` gates the act, at the control and
              // at the route.
              requiredPermission: Permission.PUBLIC_DATA_READ,
            },
            // Sprint 62.56.0 — the biometric capture registers: capture components,
            // the chain of custody of a biometric data transfer, and readiness
            // reviews. Each on the overlay's read grant; the component detail page
            // is reached from the list and has no leaf.
            {
              label: extractLocale(
                {
                  en: 'Biometric Capture Components',
                  vi: 'Thành phần thu nhận sinh trắc học',
                },
                locale,
              ),
              to: '/admin/biometric-capture/components',
              requiredPermission: Permission.BIOMETRIC_CAPTURE_READ,
            },
            {
              label: extractLocale(
                {
                  en: 'Biometric Chain-of-Custody Records',
                  vi: 'Hồ sơ chuỗi lưu giữ dữ liệu sinh trắc học',
                },
                locale,
              ),
              to: '/admin/biometric-capture/custody-transfers',
              requiredPermission: Permission.BIOMETRIC_CAPTURE_READ,
            },
            {
              label: extractLocale(
                {
                  en: 'Biometric Readiness Reviews',
                  vi: 'Rà soát mức độ sẵn sàng về sinh trắc học',
                },
                locale,
              ),
              to: '/admin/biometric-capture/readiness',
              requiredPermission: Permission.BIOMETRIC_CAPTURE_READ,
            },
            // Sprint 62.56.0 round 2 (PU-4) — the supervision and inspection acts.
            {
              label: extractLocale(
                {
                  en: 'Biometric Supervision and Inspection Records',
                  vi: 'Hồ sơ hướng dẫn, theo dõi và kiểm tra về sinh trắc học',
                },
                locale,
              ),
              to: '/admin/biometric-capture/supervision-records',
              requiredPermission: Permission.BIOMETRIC_CAPTURE_READ,
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.sector_overlays_digital_technology,
          icon: 'monitor',
          children: [
            // Sprint 62.54.0 — applications filed for a licence, certificate or
            // appraisal, with the fee lines paid and the authority's decision.
            {
              label: extractLocale(
                {
                  en: 'Licence Applications and Fees',
                  vi: 'Hồ sơ đề nghị cấp phép và phí, lệ phí',
                },
                locale,
              ),
              to: '/admin/sector-licences/applications',
              // Sprint 62.55.0 (UC-VN-109 F-2) — no leaf gate of its own: shown on
              // its route's grants (`isLinkVisible`), the licence read or the
              // cross-register fee-line read.
            },
            // Sprint 62.55.0 (UC-VN-109 F-3) — the fee and charge lines of one
            // paid-on month across applications and sender-name certificates.
            {
              label: extractLocale(
                { en: 'Fee Lines by Period', vi: 'Các khoản phí, lệ phí theo kỳ' },
                locale,
              ),
              to: '/admin/sector-licences/fee-lines',
              requiredPermission: Permission.REGULATORY_FEE_LINE_READ,
            },
            // Sprint 62.54.0 — the sender-identity side: sender-name certificates
            // and the charges paid for them. Never consent or suppression evidence.
            {
              label: t.consent.nav.marketing_sender_name_certificates,
              to: '/admin/marketing-communications/sender-name-certificates',
              // Sprint 62.55.0 (UC-VN-109 F-2) — no leaf gate of its own: shown on
              // its route's grants, the marketing read or the fee-line read.
            },
            {
              label: extractLocale({ en: 'Seller Register', vi: 'Sổ đăng ký người bán' }, locale),
              to: '/admin/platform-evidence/sellers',
              requiredPermission: Permission.PLATFORM_EVIDENCE_READ,
            },
            {
              label: extractLocale(
                { en: 'Transaction Reconciliation', vi: 'Đối soát giao dịch' },
                locale,
              ),
              to: '/admin/platform-evidence/reconciliation',
              requiredPermission: Permission.PLATFORM_EVIDENCE_READ,
            },
            {
              // **Not** "Authority Requests". `/admin/incidents/authority-requests`
              // already ships that exact English label, and 64.3.0 measured that
              // page names are not unique within a module — which is what makes
              // a name-based join between the sidebar and the documentation
              // unusable. Two leaves with one name is a leaf a user cannot find.
              label: extractLocale(
                { en: 'Authority Correspondence', vi: 'Trao đổi với cơ quan có thẩm quyền' },
                locale,
              ),
              to: '/admin/platform-evidence/authority-requests',
              // `:read`. Seeing what was answered is not answering — the three
              // routes that produce something an authority reads carry
              // `platform_evidence:respond`, at the route and at the control.
              requiredPermission: Permission.PLATFORM_EVIDENCE_READ,
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.sector_overlays_publishing,
          icon: 'book',
          children: [
            {
              label: extractLocale({ en: 'Publications', vi: 'Xuất bản phẩm' }, locale),
              to: '/admin/publications',
              requiredPermission: Permission.PUBLICATION_READ,
            },
            {
              label: extractLocale(
                { en: 'Publication Assessments', vi: 'Đánh giá xuất bản phẩm' },
                locale,
              ),
              to: '/admin/publication-assessments',
              requiredPermission: Permission.PUBLICATION_ASSESSMENT_READ,
            },
            {
              label: extractLocale(
                { en: 'Legal Representatives', vi: 'Người đại diện theo pháp luật' },
                locale,
              ),
              to: '/admin/legal-representatives',
              requiredPermission: Permission.LEGAL_REPRESENTATIVE_READ,
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.sector_overlays_education,
          icon: 'graduation',
          children: [
            {
              label: extractLocale({ en: 'Student Records', vi: 'Học bạ số' }, locale),
              to: '/admin/student-records',
              requiredPermission: Permission.ISSUED_RECORD_READ,
            },
          ],
        },
        {
          label: t.admin.sidebar_groups.sector_overlays_cross_sector,
          icon: 'hub',
          children: [
            {
              label: extractLocale({ en: 'Assessment Periods', vi: 'Kỳ đánh giá' }, locale),
              to: '/admin/cybersecurity-assurance-index/periods',
              requiredPermission: Permission.CYBERSECURITY_ASSURANCE_INDEX_READ,
            },
            {
              label: extractLocale(
                {
                  en: 'Direction and Management Statistics',
                  vi: 'Số liệu phục vụ chỉ đạo, điều hành',
                },
                locale,
              ),
              to: '/admin/cybersecurity-assurance-index/statistics',
              requiredPermission: Permission.CYBERSECURITY_ASSURANCE_INDEX_READ,
            },
            {
              label: extractLocale(
                {
                  en: 'Supplier Evidence Requests',
                  vi: 'Yêu cầu nhà cung cấp cung cấp tài liệu chứng minh',
                },
                locale,
              ),
              to: '/admin/cybersecurity-assurance-index/supplier-requests',
              requiredPermission: Permission.CYBERSECURITY_ASSURANCE_INDEX_READ,
            },
          ],
        },
      ],
    },
    {
      label: t.admin.nav.task_management,
      icon: 'monitoring',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.task_management,
      children: [
        { label: t.task_management.nav.all_tasks, to: '/admin/tasks' },
        { label: t.task_management.nav.board, to: '/admin/tasks/board' },
        { label: t.task_management.nav.calendar, to: '/admin/tasks/calendar' },
        { label: t.task_management.nav.my_tasks, to: '/admin/tasks/my' },
        { label: t.task_management.nav.overdue, to: '/admin/tasks/overdue' },
        { label: t.task_management.nav.workflows, to: '/admin/tasks/workflows' },
      ],
    },
    {
      label: t.admin.nav.vendor_risk,
      icon: 'governance',
      // No section gate: each leaf is shown on its own route's grant (`isLinkVisible`).
      moduleId: SIDEBAR_MODULE_MAP.vendor_risk,
      children: [
        {
          label: t.admin.sidebar_groups.vr_overview,
          children: [
            { label: t.vendor_risk.nav.dashboard, to: '/admin/vendor-risk/dashboard' },
            { label: t.vendor_risk.nav.portfolio, to: '/admin/vendor-risk/portfolio' },
          ],
        },
        {
          label: t.admin.sidebar_groups.vr_onboarding,
          children: [
            {
              label: t.vendor_risk.nav.onboarding_cases,
              to: '/admin/vendor-risk/onboarding-cases',
            },
            { label: t.vendor_risk.nav.gates, to: '/admin/vendor-risk/gates' },
            // Sprint 47.5.8.1 — Contracts is its own first-class module; sidebar entry stays
            // nested under Vendor & Third-Party Risk for affinity but the route is flat.
            { label: t.vendor_risk.nav.contracts, to: '/admin/contracts' },
          ],
        },
        {
          label: t.admin.sidebar_groups.vr_assessment,
          children: [
            { label: t.vendor_risk.nav.questionnaires, to: '/admin/vendor-risk/questionnaires' },
            { label: t.vendor_risk.nav.scoring, to: '/admin/vendor-risk/scoring' },
            { label: t.vendor_risk.nav.reviews, to: '/admin/vendor-risk/reviews' },
            { label: t.vendor_risk.nav.reassessments, to: '/admin/vendor-risk/reassessments' },
          ],
        },
        {
          label: t.admin.sidebar_groups.vr_oversight,
          children: [
            { label: t.vendor_risk.nav.signals, to: '/admin/vendor-risk/signals' },
            { label: t.vendor_risk.nav.acceptances, to: '/admin/vendor-risk/acceptances' },
            { label: t.vendor_risk.nav.review_routing, to: '/admin/vendor-risk/review-routing' },
            {
              label: t.vendor_risk.nav.sub_processor_register,
              to: '/admin/vendor-risk/sub-processor-register',
            },
          ],
        },
      ],
    },
    // Help Center always last
    { label: t.admin.nav.help_center, to: '/admin/help', icon: 'help' },
  ];

  /** The section's own gate (none declared = held). */
  const sectionGateHeld = useCallback(
    (item: MenuItem) => !item.requiredPermission || hasPermission(item.requiredPermission),
    [hasPermission],
  );
  /**
   * A leaf is shown when its own and its route's gates are held AND either
   * its section's gate is held or it is a self-service page (Sprint 63.22.0
   * gate E12.5 pass 2 — My Reviews and Notification Preferences sat under the
   * Platform section's gate, which their pages and endpoints never ask for).
   * A sector overlay leaf is also shown only while an operational overlay
   * declares into it (Sprint 63.43.0, `hiddenOverlayRoutes`).
   */
  const leafVisibleIn = useCallback(
    (leaf: MenuLeaf, sectionOpen: boolean) =>
      (sectionOpen || leaf.selfService === true) &&
      !hiddenOverlayRoutes.has(leaf.to) &&
      isLinkVisible(leaf, hasPermission),
    [hasPermission, hiddenOverlayRoutes],
  );

  // Filter sidebar sections by permission AND module enabled state
  const visibleMenuItems = useMemo(
    () =>
      menuItems.filter((item) => {
        // Check RBAC permission — the item's own gate, and for a standalone
        // link the gate of the route it opens. A section whose gate is not
        // held still shows when a self-service leaf in it is visible, and then
        // shows only those leaves (`leafVisibleIn`).
        const sectionOpen = sectionGateHeld(item);
        if (!sectionOpen && !item.children) return false;
        if (!item.children && !isLinkVisible(item, hasPermission)) return false;
        // Check module enabled state
        if (item.moduleId && !isModuleEnabled(item.moduleId)) return false;
        if (item.moduleId === SIDEBAR_MODULE_MAP.sector_overlays && sectorOverlaysNotEntitled)
          return false;
        // Sprint 63.22.0 (OD-A6) — a group shows when any of its leaves does.
        // A group whose every leaf is gated away is an empty heading.
        if (item.children) {
          const leaves = flattenLeaves(item.children);
          if (leaves.length === 0) return sectionOpen;
          if (!leaves.some((l) => leafVisibleIn(l, sectionOpen))) return false;
        }
        return true;
      }),
    [
      menuItems,
      hasPermission,
      isModuleEnabled,
      sectionGateHeld,
      leafVisibleIn,
      sectorOverlaysNotEntitled,
    ],
  );

  // Sprint 63.42.2 (AC2) — the active leaf's ancestors, read from the menu
  // tree (`findActiveTrail`): its section, its group and its category expand.
  const settledTrail = useMemo(
    () =>
      findActiveTrail(visibleMenuItems, location.pathname, (item, leaf) =>
        leafVisibleIn(leaf, sectionGateHeld(item)),
      ),
    [location.pathname, visibleMenuItems, leafVisibleIn, sectionGateHeld],
  );
  // While the overlay projection loads every sector leaf is hidden, so a URL a
  // longer sector leaf also matches would first settle on the shorter drawn
  // leaf and open its category, then open Sector Overlays when the answer came:
  // two categories open (gate E12.5 pass 2, #3). Nothing expands until the
  // answer decides which leaf is drawn.
  const trailPending = useMemo(
    () =>
      sectorOverlaysLoading &&
      SECTOR_OVERLAY_LEAF_VIEWS.some(
        (view) =>
          leafMatches(location.pathname, view.to) &&
          (settledTrail === null || view.to.length > settledTrail.leaf.to.length),
      ),
    [sectorOverlaysLoading, location.pathname, settledTrail],
  );
  const activeTrail = trailPending ? null : settledTrail;

  // Auto-expand the section that contains the active route
  const getInitialExpanded = useCallback(() => {
    const expanded: Record<string, boolean> = {};
    if (activeTrail) expanded[sectionKey(activeTrail.item)] = true;
    return expanded;
  }, [activeTrail]);

  // Keyed by `sectionKey` (P3-1), never by position among visible sections.
  const [expanded, setExpanded] = useState<Record<string, boolean>>(getInitialExpanded);

  const toggleSection = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Auto-expand the sub-group (3rd tier) that contains the active route.
  const getInitialSubExpanded = useCallback(() => {
    const sub: Record<string, boolean> = {};
    if (activeTrail?.group) sub[subGroupKey(activeTrail.item, activeTrail.group)] = true;
    return sub;
  }, [activeTrail]);

  const [subExpanded, setSubExpanded] = useState<Record<string, boolean>>(getInitialSubExpanded);

  // Layouts survive client navigation, so mount-time defaults alone leave the
  // destination hidden under the previous page's sidebar. Depend on ancestor
  // membership rather than menu object identity: same-route rerenders must not
  // undo a person's deliberate collapse.
  // Keys are routes, which may hold ',' — joined on a newline instead.
  const activeSectionKeys = Object.keys(getInitialExpanded()).join('\n');
  const activeSubGroupKeys = Object.keys(getInitialSubExpanded()).join('\n');
  useEffect(() => {
    if (activeSectionKeys) {
      setExpanded((prev) => {
        const next = { ...prev };
        for (const key of activeSectionKeys.split('\n')) next[key] = true;
        return next;
      });
    }
    if (activeSubGroupKeys) {
      setSubExpanded((prev) => {
        const next = { ...prev };
        for (const key of activeSubGroupKeys.split('\n')) next[key] = true;
        return next;
      });
    }
  }, [location.pathname, activeSectionKeys, activeSubGroupKeys]);

  const toggleSubGroup = (key: string) => {
    setSubExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Sprint 25.3.4: Category fold/unfold state
  const [categoryExpanded, setCategoryExpanded] =
    useState<Record<string, boolean>>(loadCategoryState);

  // Auto-expand the category containing the active route: the active leaf's
  // category (AC2). A URL no leaf matches keeps the category-prefix rule.
  const activeCategoryKey = useMemo(() => {
    if (trailPending) return null;
    if (activeTrail) {
      if (activeTrail.category) return activeTrail.category;
      // A standalone section drawn like a category opens on the same rule.
      return activeTrail.item.children ? standaloneFoldKey(activeTrail.item) : null;
    }
    for (const cat of SIDEBAR_CATEGORIES) {
      if (cat.modules.some((m) => location.pathname.startsWith(m))) return cat.key;
    }
    return null;
  }, [trailPending, activeTrail, location.pathname]);

  // Expand on navigation, while permitting manual collapse on the current page.
  useEffect(() => {
    if (activeCategoryKey) {
      setCategoryExpanded((prev) => ({ ...prev, [activeCategoryKey]: true }));
    }
  }, [location.pathname, activeCategoryKey]);

  const toggleCategory = useCallback((key: string) => {
    setCategoryExpanded((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveCategoryState(next);
      return next;
    });
  }, []);

  // Group visible items by category
  const { standalone, categorized } = useMemo(() => {
    const standaloneItems: { item: MenuItem }[] = [];
    const catMap: Record<string, { item: MenuItem }[]> = {};
    for (const cat of SIDEBAR_CATEGORIES) catMap[cat.key] = [];

    visibleMenuItems.forEach((item) => {
      const catKey = getCategoryForItem(item);
      if (catKey && catMap[catKey]) {
        catMap[catKey].push({ item });
      } else {
        standaloneItems.push({ item });
      }
    });
    return { standalone: standaloneItems, categorized: catMap };
  }, [visibleMenuItems]);

  const categoryLabels: Record<string, string> = {
    data_privacy_rights:
      (t.admin as unknown as Record<string, string>)?.sidebar_category_data_privacy_rights ??
      'Data Privacy & Rights',
    vendor_third_party:
      (t.admin as unknown as Record<string, string>)?.sidebar_category_vendor_third_party ??
      'Vendor & Third-Party Management',
    risk_assessment:
      (t.admin as unknown as Record<string, string>)?.sidebar_category_risk_assessment ??
      'Risk & Assessment',
    operations_automation:
      (t.admin as unknown as Record<string, string>)?.sidebar_category_operations_automation ??
      'Operations & Automation',
    sector_overlays:
      (t.admin as unknown as Record<string, string>)?.sidebar_category_sector_overlays ??
      'Sector Overlays',
    task_workflow:
      (t.admin as unknown as Record<string, string>)?.sidebar_category_task_workflow ??
      'Task & Workflow Management',
  };

  // ── Sidebar render helpers (shared by categorized + standalone paths) ───
  const Chevron = () => (
    <svg className="nav-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M6 4l4 4-4 4"
        style={{ stroke: 'var(--color-accent-on-dark)' }}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const visibleLeaves = (leaves: MenuLeaf[], sectionOpen: boolean) =>
    leaves.filter((l) => leafVisibleIn(l, sectionOpen));

  /**
   * A leaf matches only its exact route when a sibling's route is under it, or
   * when the URL also opens a longer leaf elsewhere in the menu: the active
   * trail's leaf is the one current link (gate E12.5 pass 1, F7 — on
   * `/admin/sector-licences/applications` both Regulatory › Sector Licences and
   * the Digital Technology leaf carried `aria-current="page"`). The trail's
   * own leaf never matches exactly only, so a page under it (`/admin/tasks/:id`)
   * or a trailing-slash URL keeps its current link (gate E12.5 pass 2, #4).
   */
  const needsEndFor = (leaf: MenuLeaf, allLeaves: MenuLeaf[]) =>
    activeTrail?.leaf.to !== leaf.to &&
    (allLeaves.some((sib) => sib.to !== leaf.to && sib.to.startsWith(leaf.to + '/')) ||
      (activeTrail !== null &&
        activeTrail.leaf.to !== leaf.to &&
        leafMatches(location.pathname, leaf.to)));

  const renderLeaf = (leaf: MenuLeaf, allLeaves: MenuLeaf[], withIcon = false) => (
    <li key={leaf.to}>
      <NavLink to={leaf.to} end={needsEndFor(leaf, allLeaves)} data-testid={leaf.testId}>
        {withIcon && leaf.icon && <MenuIcon name={leaf.icon} />}
        {leaf.label}
      </NavLink>
    </li>
  );

  const renderModuleContents = (item: MenuItem) => {
    if (!item.children) return null;
    const allLeaves = flattenLeaves(item.children);
    const sectionOpen = sectionGateHeld(item);
    return (
      <ul className={`nav-sub${expanded[sectionKey(item)] ? ' nav-sub-open' : ''}`}>
        {item.children.map((child, cIdx) => {
          if (isSubGroup(child)) {
            const subLeaves = visibleLeaves(child.children, sectionOpen);
            if (subLeaves.length === 0) return null;
            const key = subGroupKey(item, child);
            const open = !!subExpanded[key];
            return (
              <li key={`sg-${cIdx}`} className="nav-subgroup">
                <button
                  className={`nav-subgroup-toggle${open ? ' open' : ''}`}
                  onClick={() => toggleSubGroup(key)}
                  aria-expanded={open}
                >
                  <span className="nav-subgroup-label">{child.label}</span>
                  <Chevron />
                </button>
                <ul className={`nav-subsub${open ? ' nav-subsub-open' : ''}`}>
                  {subLeaves.map((leaf) => renderLeaf(leaf, allLeaves))}
                </ul>
              </li>
            );
          }
          if (!leafVisibleIn(child, sectionOpen)) return null;
          return renderLeaf(child, allLeaves);
        })}
      </ul>
    );
  };

  const renderGroupedModule = (item: MenuItem) => (
    <li key={sectionKey(item)} className="nav-group">
      <button
        className={`nav-group-toggle${expanded[sectionKey(item)] ? ' open' : ''}`}
        onClick={() => toggleSection(sectionKey(item))}
        aria-expanded={!!expanded[sectionKey(item)]}
      >
        <span className="nav-group-label">
          {item.icon && <MenuIcon name={item.icon} />}
          {item.label}
        </span>
        <Chevron />
      </button>
      {renderModuleContents(item)}
    </li>
  );

  /**
   * Sprint 63.42.2 (AC1) — a section whose category flattens it
   * (`flattenSections`): its groups render at the section tier, directly under
   * the category header, each a disclosure button over its leaves; a direct
   * leaf renders as a link at that tier. Same visibility rules as
   * `renderModuleContents`: a group with no visible leaf is not drawn.
   */
  const flattenedEntries = (item: MenuItem): JSX.Element[] => {
    if (!item.children) {
      return [
        <li key={sectionKey(item)}>
          <NavLink to={item.to!}>
            {item.icon && <MenuIcon name={item.icon} />}
            {item.label}
          </NavLink>
        </li>,
      ];
    }
    const allLeaves = flattenLeaves(item.children);
    const sectionOpen = sectionGateHeld(item);
    const entries: JSX.Element[] = [];
    item.children.forEach((child) => {
      if (!isSubGroup(child)) {
        if (leafVisibleIn(child, sectionOpen)) entries.push(renderLeaf(child, allLeaves, true));
        return;
      }
      const subLeaves = visibleLeaves(child.children, sectionOpen);
      if (subLeaves.length === 0) return;
      const key = subGroupKey(item, child);
      const open = !!subExpanded[key];
      const listId = `nav-group-${key.replace(/[^A-Za-z0-9_-]/g, '-')}`;
      entries.push(
        <li key={key} className="nav-group">
          <button
            className={`nav-group-toggle${open ? ' open' : ''}`}
            onClick={() => toggleSubGroup(key)}
            aria-expanded={open}
            aria-controls={listId}
          >
            <span className="nav-group-label">
              {child.icon && <MenuIcon name={child.icon} />}
              {child.label}
            </span>
            <Chevron />
          </button>
          <ul id={listId} className={`nav-sub${open ? ' nav-sub-open' : ''}`}>
            {subLeaves.map((leaf) => renderLeaf(leaf, allLeaves))}
          </ul>
        </li>,
      );
    });
    return entries;
  };

  /**
   * Owner request 2026-11-02 — a standalone section with children (Forseti,
   * Onboarding, Platform) is drawn as a top-level entry in the same form as a
   * category: the category header (icon, uppercase label, +/−) over the
   * section's own groups and leaves at the tier below (`flattenedEntries`).
   * Presentation only: the section stays standalone in the menu tree (it is no
   * `SIDEBAR_CATEGORIES` key, so the Module Status strip and the manual's
   * menu tree are unchanged). Its open state is the categories' fold state
   * (`standaloneFoldKey`): remembered between visits, opened on navigation to
   * one of its leaves, and closable by hand on the current page.
   */
  const renderStandaloneSection = (item: MenuItem) => {
    const key = sectionKey(item);
    const foldKey = standaloneFoldKey(item);
    const isOpen = categoryExpanded[foldKey] === true;
    const entries = flattenedEntries(item);
    const slug = key.replace(/^\/+/, '').replace(/[^A-Za-z0-9_-]/g, '-');
    const headerId = `section-${slug}-header`;
    const groupId = `section-${slug}-items`;
    return (
      <li key={key} className="nav-category">
        <button
          id={headerId}
          className={`nav-category-toggle${isOpen ? ' open' : ''}`}
          onClick={() => toggleCategory(foldKey)}
          aria-expanded={isOpen}
          aria-controls={groupId}
          aria-label={`${item.label}, ${(entries.length === 1 ? t.common.items_count_one : t.common.items_count).replace('{count}', String(entries.length))}`}
        >
          <span className="nav-category-label">
            {item.icon && <MenuIcon name={item.icon} />}
            {item.label}
          </span>
          <span style={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1 }}>
            {isOpen ? '−' : '+'}
          </span>
        </button>
        {isOpen && (
          <ul id={groupId} aria-labelledby={headerId}>
            {entries}
          </ul>
        )}
      </li>
    );
  };

  return (
    // Sprint 63.22.0 — the dock owns the bottom-right corner for every
    // occupant (the Forseti launcher, and the UI Assist toolbar when enabled),
    // so it wraps the UI Assist provider that renders the overlay. The page
    // context wraps the routed pages, which register their record, and the
    // launcher, which reads it.
    <BottomRightDockProvider>
      <ForsetiPageProvider>
        <UIAssistProvider>
          <TourProvider>
            <div className="admin-layout" data-testid="page-ready">
              <SkipToContent />
              {/* Sprint 25.3.4.1: Mobile hamburger button */}
              <button
                ref={hamburgerRef}
                className="mobile-hamburger"
                onClick={() => setMobileMenuOpen((v) => !v)}
                aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={mobileMenuOpen}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  {mobileMenuOpen ? (
                    <path
                      d="M6 6l12 12M6 18L18 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  ) : (
                    <path
                      d="M4 6h16M4 12h16M4 18h16"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  )}
                </svg>
              </button>
              {/* Sprint 25.3.4.1: Mobile overlay backdrop */}
              {mobileMenuOpen && (
                <div
                  className="mobile-sidebar-backdrop"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-hidden="true"
                />
              )}
              <nav
                ref={sidebarRef}
                className={`admin-sidebar${mobileMenuOpen ? ' mobile-open' : ''}`}
                aria-label={t.common.sidebar_navigation}
              >
                <div className="admin-logo">
                  <img
                    src="/logo_compliance.png"
                    alt="AesirX ComplianceOne"
                    style={{ height: 80, width: 'auto' }}
                  />
                </div>
                <ul className="admin-nav">
                  {/* Dashboard (standalone at top) */}
                  {standalone
                    .filter(({ item }) => item.to === '/admin')
                    .map(({ item }) => (
                      <li key={sectionKey(item)}>
                        <NavLink to={item.to!} end>
                          {item.icon && <MenuIcon name={item.icon} />}
                          {item.label}
                        </NavLink>
                      </li>
                    ))}

                  {/* Sprint 25.3.4: Categorized module groups */}
                  {SIDEBAR_CATEGORIES.map((cat) => {
                    const items = categorized[cat.key];
                    if (!items || items.length === 0) return null;
                    const isOpen = categoryExpanded[cat.key] === true;
                    // Sprint 63.42.2 (AC1) — a flattened category lists its
                    // section's groups, and counts them, as its entries.
                    const flat = categoryFlattensSections(cat)
                      ? items.flatMap(({ item }) => flattenedEntries(item))
                      : null;
                    const entryCount = flat ? flat.length : items.length;
                    const headerId = `category-${cat.key}-header`;
                    const groupId = `category-${cat.key}-items`;
                    return (
                      <li key={cat.key} className="nav-category">
                        <button
                          id={headerId}
                          className={`nav-category-toggle${isOpen ? ' open' : ''}`}
                          onClick={() => toggleCategory(cat.key)}
                          aria-expanded={isOpen}
                          aria-controls={groupId}
                          aria-label={`${categoryLabels[cat.key]}, ${(entryCount === 1 ? t.common.items_count_one : t.common.items_count).replace('{count}', String(entryCount))}`}
                        >
                          <span className="nav-category-label">
                            <MenuIcon name={cat.icon} />
                            {categoryLabels[cat.key]}
                          </span>
                          <span style={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1 }}>
                            {isOpen ? '−' : '+'}
                          </span>
                        </button>
                        {/* Sprint 62.52.0 Remediation W — no `role="group"`: a list
                            whose role is overridden is no list, and every `<li>` in
                            it failed axe `listitem` (seven on the framework page).
                            It stays a list, named by its category's button. */}
                        {isOpen && (
                          <ul id={groupId} aria-labelledby={headerId}>
                            {flat ??
                              items.map(({ item }) =>
                                item.children ? (
                                  renderGroupedModule(item)
                                ) : (
                                  <li key={sectionKey(item)}>
                                    <NavLink to={item.to!}>
                                      {item.icon && <MenuIcon name={item.icon} />}
                                      {item.label}
                                    </NavLink>
                                  </li>
                                ),
                              )}
                          </ul>
                        )}
                      </li>
                    );
                  })}

                  {/* Forseti, Onboarding, Platform + Help Center (standalone at
                      bottom). A section with children is drawn like a category. */}
                  {standalone
                    .filter(({ item }) => item.to !== '/admin')
                    .map(({ item }) =>
                      item.children ? (
                        renderStandaloneSection(item)
                      ) : (
                        <li key={sectionKey(item)}>
                          <NavLink to={item.to!}>
                            {item.icon && <MenuIcon name={item.icon} />}
                            {item.label}
                          </NavLink>
                        </li>
                      ),
                    )}
                </ul>
                <div className="admin-locale-switcher">
                  <select
                    value={locale}
                    onChange={(e) => setLocale(e.target.value)}
                    aria-label={t.common.select_language ?? 'Select language'}
                  >
                    {languages.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                {user && (
                  <div className="admin-user-info">
                    <span>{user.role_name}</span>
                    <button onClick={logout} className="btn-logout" aria-label={t.common.logout}>
                      {t.common.logout}
                    </button>
                  </div>
                )}
              </nav>
              <main
                id="main-content"
                tabIndex={-1}
                className="admin-content"
                data-module={activeModule}
              >
                {/* Sprint 61.0.10 — the dashboard header renders its own bell. */}
                {user && activeModule !== 'dashboard' && (
                  <div className="admin-topbar">
                    <NotificationBell t={t} />
                  </div>
                )}
                <Outlet />
                {/* Sprint 63.22.0 — the corner dock (fixed; inside <main> so its
                    occupants belong to the main landmark). */}
                <BottomRightDock hidden={mobileMenuOpen} />
              </main>
            </div>
            <GlobalTourRunner />
            <ForsetiLauncher />
          </TourProvider>
        </UIAssistProvider>
      </ForsetiPageProvider>
    </BottomRightDockProvider>
  );
}

/** Renders TourRunner at layout level so it survives route changes. */
function GlobalTourRunner() {
  const tour = useTourContext();
  // The toast hook is read only while a tour runs, inside the product's
  // ToastProvider; layout renders with no tour need no provider.
  if (!tour.activeScript) return null;
  return <ActiveTourRunner />;
}

/**
 * Sprint 63.39.0 AC7 — a failed completion or dismissal is said, not
 * swallowed. Before this the error was ignored and the tour silently read
 * "not started" on the next visit; a 409 `tour_revision_conflict` (the tour
 * changed while the user was in it) now tells them to restart it.
 */
const TOUR_REVISION_CONFLICT = 'tour_revision_conflict';

function ActiveTourRunner() {
  const { activeScript, activeTourMode, startStepIndex, launchId, clearTour } = useTourContext();
  const { t } = useLocale();
  const toast = useToast();
  const latestLaunchRef = useRef(launchId);
  latestLaunchRef.current = launchId;

  const reportTourWriteFailure = useCallback(
    (err: unknown) => {
      console.error('Failed to record the tour outcome:', err);
      const e = err as { status?: number | null; serverCode?: string } | null;
      if (e?.serverCode === TOUR_REVISION_CONFLICT) {
        toast.error(t.onboarding.tours.revision_conflict);
        return;
      }
      toast.error(extractErrorMessage(err, t.common.operation_failed));
    },
    [t, toast],
  );

  const handleComplete = useCallback(
    async (module: string, revision?: string) => {
      try {
        await apiClient.completeTour(module, revision);
      } catch (err) {
        reportTourWriteFailure(err);
      }
      if (latestLaunchRef.current === launchId) clearTour();
    },
    [clearTour, launchId, reportTourWriteFailure],
  );

  const handleExit = useCallback(async () => {
    if (activeScript) {
      try {
        await apiClient.dismissTour(activeScript.module);
      } catch (err) {
        reportTourWriteFailure(err);
      }
    }
    if (latestLaunchRef.current === launchId) clearTour();
  }, [activeScript, clearTour, launchId, reportTourWriteFailure]);

  const handleStepChange = useCallback(
    async (module: string, stepIndex: number, revision?: string) => {
      try {
        await apiClient.advanceTour(module, stepIndex, revision);
      } catch (err) {
        // Losing the resume point must not interrupt the tour itself.
        console.error('Failed to store the tour step reached:', err);
      }
    },
    [],
  );

  if (!activeScript) return null;

  return (
    <TourRunner
      key={launchId}
      script={activeScript}
      mode={activeTourMode}
      startStepIndex={startStepIndex}
      onComplete={handleComplete}
      onExit={handleExit}
      onStepChange={handleStepChange}
    />
  );
}
